/**
 * The sound catalog: the fixed set of bundled click sounds the user can
 * choose from. Each entry has a stable id (used for persistence, so it must
 * never change), the wav file it loads from, and a friendly display name.
 */ const $b7bbc4f8d421aeee$export$86f376722808d38b = [
    {
        id: 'click',
        name: 'Click',
        file: 'assets/click.wav'
    },
    {
        id: 'sine_1',
        name: 'Sine 1',
        file: 'assets/sine_1.wav'
    },
    {
        id: 'sine_2',
        name: 'Sine 2',
        file: 'assets/sine_2.wav'
    },
    {
        id: 'wood_1',
        name: 'Wood 1',
        file: 'assets/wood_1.wav'
    },
    {
        id: 'wood_2',
        name: 'Wood 2',
        file: 'assets/wood_2.wav'
    },
    {
        id: 'noise_click',
        name: 'Noise Click',
        file: 'assets/noise_click.wav'
    }
];
const $b7bbc4f8d421aeee$export$eb5f9c6e38f3e159 = 'wood_2';
const $b7bbc4f8d421aeee$export$67fdbea1e102c657 = 'click';
function $b7bbc4f8d421aeee$export$d50e2d36029e4cac(value) {
    return typeof value === 'string' && $b7bbc4f8d421aeee$export$86f376722808d38b.some((sound)=>sound.id === value);
}
/**
 * Coerce a persisted value to a valid sound id, falling back to the default
 * when it is missing or no longer in the catalog.
 */ function $b7bbc4f8d421aeee$export$8bcf5b6f3ecd9d6d(value, fallback) {
    return $b7bbc4f8d421aeee$export$d50e2d36029e4cac(value) ? value : fallback;
}
function $b7bbc4f8d421aeee$export$16378f75946b63b2(id) {
    return $b7bbc4f8d421aeee$export$86f376722808d38b.find((sound)=>sound.id === id)?.name ?? id;
}


/**
 * The Settings bottom sheet: slides up from the bottom of the screen, with
 * segmented Downbeat/Secondary tabs that each list the full sound catalog as
 * big touch rows (native radio inputs). One list is visible at a time, and
 * each tab echoes its current choice.
 *
 * Standard modal behaviour: focus trap, Escape and backdrop click close, and
 * focus is restored to the gear button on close.
 *
 * The markup lives in index.html; this class wires it up and renders the
 * sound rows from the catalog.
 */ class $d8caa695d74baecf$export$ee3e5af31f081424 {
    options;
    backdrop;
    sheet;
    panel;
    openButton;
    soundList;
    tabs;
    activeSection = 'downbeat';
    isOpen = false;
    lastFocused = null;
    constructor(options){
        this.options = options;
        this.backdrop = document.getElementById('settings-backdrop');
        this.sheet = document.getElementById('settings-sheet');
        this.panel = document.getElementById('settings-panel');
        this.openButton = document.getElementById('settings-open');
        this.soundList = document.getElementById('settings-sounds');
        this.tabs = Array.from(this.backdrop.querySelectorAll('.settings-tab'));
        this.openButton.addEventListener('click', ()=>this.open());
        this.backdrop.querySelector('.sheet-close').addEventListener('click', ()=>this.close());
        this.backdrop.addEventListener('click', (event)=>{
            if (event.target === this.backdrop) this.close();
        });
        this.backdrop.addEventListener('keydown', (event)=>this.onKeydown(event));
        this.tabs.forEach((tab)=>{
            tab.addEventListener('click', ()=>this.selectSection(tab.dataset.section));
        });
        this.selectSection('downbeat');
        this.syncEchoes();
    }
    open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.lastFocused = document.activeElement;
        // always reopen on the Downbeat tab
        this.selectSection('downbeat');
        this.backdrop.hidden = false;
        // add the class on the next frame so the slide-up transition runs
        requestAnimationFrame(()=>this.backdrop.classList.add('open'));
        document.body.classList.add('sheet-open');
        this.sheet.focus();
    }
    close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.backdrop.classList.remove('open');
        document.body.classList.remove('sheet-open');
        const hide = ()=>{
            if (!this.isOpen) this.backdrop.hidden = true;
        };
        this.sheet.addEventListener('transitionend', hide, {
            once: true
        });
        // fallback in case the transition is interrupted and never ends
        setTimeout(hide, 300);
        (this.lastFocused ?? this.openButton).focus();
    }
    onKeydown(event) {
        if (event.key === 'Escape') {
            event.stopPropagation();
            this.close();
            return;
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            // tablist arrow-key navigation, with automatic activation
            const target = event.target;
            if (target.classList.contains('settings-tab')) {
                event.preventDefault();
                const index = this.tabs.indexOf(target);
                const direction = event.key === 'ArrowLeft' ? -1 : 1;
                const next = this.tabs[(index + direction + this.tabs.length) % this.tabs.length];
                this.selectSection(next.dataset.section);
                next.focus();
            }
            return;
        }
        if (event.key !== 'Tab') return;
        // focus trap
        const focusables = Array.from(this.backdrop.querySelectorAll('button, input, [href], [tabindex]')).filter((el)=>!el.hasAttribute('disabled') && el.tabIndex >= 0);
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }
    /**
     * Switch the visible sound list to the given section, keeping the tab
     * states (aria-selected, roving tabindex) and the panel label in sync.
     */ selectSection(section) {
        this.activeSection = section;
        this.tabs.forEach((tab)=>{
            const isActive = tab.dataset.section === section;
            tab.classList.toggle('active', isActive);
            tab.setAttribute('aria-selected', '' + isActive);
            tab.tabIndex = isActive ? 0 : -1;
        });
        this.panel.setAttribute('aria-labelledby', `settings-tab-${section}`);
        this.soundList.setAttribute('aria-label', `${section === 'downbeat' ? 'Downbeat' : 'Secondary'} sound`);
        this.renderRows();
    }
    renderRows() {
        const selection = this.options.getSelection(this.activeSection);
        this.soundList.innerHTML = '';
        for (const sound of (0, $b7bbc4f8d421aeee$export$86f376722808d38b)){
            const row = document.createElement('label');
            row.className = 'sound-row';
            row.innerHTML = `
                <input type="radio" name="${this.activeSection}-sound" value="${sound.id}">
                <span class="sound-radio" aria-hidden="true"></span>
                <span class="sound-name">${sound.name}</span>
                <svg class="sound-check" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"/></svg>`;
            const input = row.querySelector('input');
            input.checked = sound.id === selection;
            input.addEventListener('change', ()=>this.selectSound(sound.id));
            this.soundList.appendChild(row);
        }
    }
    selectSound(sound) {
        this.options.onSelect(this.activeSection, sound);
        this.echo(this.activeSection).textContent = (0, $b7bbc4f8d421aeee$export$16378f75946b63b2)(sound);
    }
    /** Refresh both tabs' current-choice echoes from the current selections. */ syncEchoes() {
        this.tabs.forEach((tab)=>{
            const section = tab.dataset.section;
            this.echo(section).textContent = (0, $b7bbc4f8d421aeee$export$16378f75946b63b2)(this.options.getSelection(section));
        });
    }
    echo(section) {
        return this.backdrop.querySelector(`#settings-tab-${section} .settings-tab-choice`);
    }
}


/**
 * Service worker registration and update detection.
 *
 * The production service worker (generated by scripts/generate-sw.cjs)
 * caches the app shell: assets precached at install, the HTML copied into
 * a runtime cache by the install-time warmer. A new deploy installs a new
 * worker that waits until every controlled client closes - a plain
 * refresh never activates it. This module detects that waiting worker and reports it via
 * `onUpdateAvailable`; the caller shows a prompt, and on accept calls
 * `activateUpdate`, which posts SKIP_WAITING to the waiting worker. The
 * controllerchange listener below then reloads the page exactly once, onto
 * the new version.
 *
 * Long-lived sessions that never navigate would never notice a deploy, so
 * the module also asks the browser to check for updates hourly and whenever
 * the page becomes visible again.
 */ const $b296d3f904179b79$var$UPDATE_CHECK_INTERVAL_MS = 3600000;
async function $b296d3f904179b79$export$c6245babf0c60892(options) {
    const reload = options.reload ?? (()=>window.location.reload());
    // indirection so Parcel doesn't try to bundle the worker as an asset -
    // it only exists in dist/ after the Workbox step. updateViaCache: 'none'
    // keeps HTTP caching (over which the static host gives us no control)
    // from ever serving a stale worker script.
    const serviceWorkerUrl = 'service-worker.js';
    const registration = await navigator.serviceWorker.register(serviceWorkerUrl, {
        updateViaCache: 'none'
    });
    // A worker may already be waiting from a previous session - catch it at startup.
    if (registration.waiting) options.onUpdateAvailable(registration.waiting);
    // ...or a new worker may install during this session.
    registration.addEventListener('updatefound', ()=>{
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', ()=>{
            // 'installed' with an existing controller means an update, not
            // the first-ever install (which needs no prompt).
            if (installing.state === 'installed' && navigator.serviceWorker.controller) options.onUpdateAvailable(installing);
        });
    });
    // After SKIP_WAITING the new worker takes control here. The guard is
    // mandatory: controllerchange can fire more than once (multiple tabs,
    // dev tools), and a reload loop must be impossible.
    let reloading = false;
    const controller = navigator.serviceWorker;
    const onControllerChange = ()=>{
        if (reloading) return;
        reloading = true;
        reload();
    };
    controller.addEventListener('controllerchange', onControllerChange);
    // Polling: hourly, and whenever the page becomes visible again.
    const interval = setInterval(()=>void registration.update(), $b296d3f904179b79$var$UPDATE_CHECK_INTERVAL_MS);
    const onVisibilityChange = ()=>{
        if (document.visibilityState === 'visible') registration.update();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    // Teardown for tests and hot-reload: detach every listener and timer
    // this registration attached.
    return ()=>{
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        controller.removeEventListener('controllerchange', onControllerChange);
    };
}
/**
 * Tell a waiting worker to activate. Only ever call this with a worker in
 * the waiting state (registration.waiting, or one reported via
 * onUpdateAvailable) - never the active worker. The controllerchange
 * listener registered above performs the reload.
 */ function $b296d3f904179b79$export$cadaaf14c55f94de(waiting) {
    waiting.postMessage({
        type: 'SKIP_WAITING'
    });
}


/**
 * The "Update available" toast: a small non-blocking banner in the bottom
 * corner, shown when a new service worker is waiting to activate. "Reload"
 * accepts the update; "Not now" dismisses it for the session (no
 * persistence - the next page load re-detects the waiting worker and shows
 * the prompt again).
 *
 * The markup lives in index.html; this class wires it up. It never moves
 * focus and never covers interactive content.
 */ class $858d7a32f3befc68$export$f16884506f2c54fe {
    prompt;
    onAccept = null;
    dismissed = false;
    constructor(){
        this.prompt = document.getElementById('update-prompt');
        document.getElementById('update-reload').addEventListener('click', ()=>this.accept());
        document.getElementById('update-dismiss').addEventListener('click', ()=>this.dismiss());
    }
    /** Show the prompt; onAccept runs when the user picks "Reload". */ show(onAccept) {
        if (this.dismissed) return;
        this.onAccept = onAccept;
        this.prompt.hidden = false;
        // add the class on the next frame so the slide-up transition runs
        requestAnimationFrame(()=>this.prompt.classList.add('open'));
    }
    accept() {
        const onAccept = this.onAccept;
        this.hide();
        onAccept?.();
    }
    dismiss() {
        this.dismissed = true;
        this.hide();
    }
    hide() {
        this.onAccept = null;
        this.prompt.classList.remove('open');
        const done = ()=>{
            if (!this.prompt.classList.contains('open')) this.prompt.hidden = true;
        };
        this.prompt.addEventListener('transitionend', done, {
            once: true
        });
        // fallback in case the transition is interrupted and never ends
        setTimeout(done, 300);
    }
}




class $c523d1e73d11fc5d$export$ce4d7b336b893c95 {
    audioContext;
    // we need this in the chain so that we can silence any scheduled clicks
    // when `stop()` is called
    gainNode;
    get currentTime() {
        return this.audioContext.currentTime;
    }
    buffers = new Map();
    primarySound;
    secondarySound;
    silentBuffer;
    hasClickBuffers;
    /**
     * @param audioContext the shared context to schedule against. Phraser
     * passes one shared with its note voice (the scheduler's clock is that
     * context's currentTime); Clicker leaves the default.
     */ constructor(primarySound, secondarySound, audioContext = new AudioContext()){
        this.audioContext = audioContext;
        this.primarySound = primarySound;
        this.secondarySound = secondarySound;
        this.silentBuffer = this.audioContext.createBuffer(1, 1, 44100);
        this.hasClickBuffers = Promise.all((0, $b7bbc4f8d421aeee$export$86f376722808d38b).map((sound)=>this.fetchWavAsBuffer(sound.file).then((buffer)=>this.buffers.set(sound.id, buffer))));
        this.gainNode = new GainNode(this.audioContext);
        this.gainNode.connect(this.audioContext.destination);
    }
    async resume() {
        await this.audioContext.resume().then(()=>this.hasClickBuffers);
    }
    /**
     * Swap the sound used for downbeat clicks. Applies live, to clicks
     * scheduled from this point on.
     */ setPrimarySound(sound) {
        this.primarySound = sound;
    }
    /**
     * Swap the sound used for non-downbeat clicks. Applies live, to clicks
     * scheduled from this point on.
     */ setSecondarySound(sound) {
        this.secondarySound = sound;
    }
    /**
     * Play a sound immediately, connected straight to the destination -
     * bypassing the scheduled-click gain node, which is silenced on `stop()`.
     * Safe to call whether or not the metronome is playing.
     */ async preview(sound) {
        await this.resume();
        const buffer = this.buffers.get(sound);
        if (!buffer) return;
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);
        source.start();
    }
    /**
     * Silence any already-scheduled audio
     */ stop() {
        // reduce to silence
        this.gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime);
        this.gainNode.disconnect();
        // recreate
        this.gainNode = new GainNode(this.audioContext);
        this.gainNode.connect(this.audioContext.destination);
    }
    click(timestamp, clickType) {
        const click = this.audioContext.createBufferSource();
        switch(clickType){
            case 'primary':
                {
                    const buffer = this.buffers.get(this.primarySound);
                    if (buffer) click.buffer = buffer;
                    break;
                }
            case 'secondary':
                {
                    const buffer = this.buffers.get(this.secondarySound);
                    if (buffer) click.buffer = buffer;
                    break;
                }
            case "silent":
                click.buffer = this.silentBuffer;
                break;
        }
        click.connect(this.gainNode);
        click.start(timestamp);
    }
    async fetchWavAsBuffer(url) {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        return await this.audioContext.decodeAudioData(arrayBuffer);
    }
}


/**
 * Shared control bindings used by both tools' composition modules:
 * slider/switch persistence bindings, the click-to-edit bpm readout, and
 * the hint toggletips. Extracted from Clicker's original Main class;
 * behaviour unchanged.
 */ /**
 * Wire up the per-control hint toggletips within a subtree: click the badge
 * to toggle its popover, click anywhere else (or press Escape) to dismiss.
 * Only one popover is open at a time.
 */ function $d37efb89f06c4220$export$c2f691affe889eb1(root = document) {
    const closers = [];
    root.querySelectorAll('.hint').forEach((hint)=>{
        const toggle = hint.querySelector('.hint-toggle');
        const popover = hint.querySelector('.hint-popover');
        if (!toggle || !popover) return;
        const close = ()=>{
            popover.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
        };
        closers.push(close);
        toggle.addEventListener('click', (event)=>{
            event.stopPropagation();
            if (popover.hidden) {
                closers.forEach((c)=>c());
                popover.hidden = false;
                toggle.setAttribute('aria-expanded', 'true');
            } else close();
        });
        popover.addEventListener('click', (event)=>event.stopPropagation());
        document.addEventListener('click', close);
        document.addEventListener('keydown', (event)=>{
            if (event.key === 'Escape' && !popover.hidden) {
                close();
                toggle.focus();
            }
        });
    });
}
/**
 * Touch/pen track-press guard for native range inputs.
 *
 * Blink moves a range input's thumb to wherever a press lands, the moment
 * it lands (crbug.com/40843079), so a page scroll on touch (or a misplaced
 * click on mouse) that happens to land on a slider silently rewrites the
 * setting. WebKit only jumps on a completed tap, which is just as unwanted.
 * The rule enforced here: only dragging the thumb changes the value;
 * track presses do nothing, and on touch the page still scrolls normally.
 *
 * For touch/pen the press cannot be suppressed without also blocking
 * scroll (preventDefault on touchstart does both), so the value is
 * snapshotted at pointerdown and restored for the rest of the gesture
 * whenever the press landed outside the thumb; scroll takeovers arrive as
 * pointercancel and get the same restore. Mouse has no scroll conflict,
 * so mousedown on the track is preventDefault'ed outright - Blink never
 * jumps the thumb under the pointer (which would also flash the thumb's
 * pointer cursor, as the pointer then hovers the jumped thumb).
 * Either way: track-click is off on every platform, thumb-drag only.
 */ function $d37efb89f06c4220$var$guardRangeTrackPress(control) {
    // the rendered thumb is 1.5rem square; keep in sync with style.css
    const THUMB_WIDTH = 24;
    // a few px of slop so near-thumb presses behave like thumb grabs
    const HIT_SLOP = 4;
    let snapshot = null;
    let offThumb = false;
    let restoring = false;
    const restore = ()=>{
        if (restoring || snapshot === null || control.valueAsNumber === snapshot) return;
        restoring = true;
        control.valueAsNumber = snapshot;
        // re-sync the <output> displays wired up via the inline oninput handlers
        control.dispatchEvent(new Event('input'));
        restoring = false;
    };
    const pressOffThumb = (clientX)=>{
        const rect = control.getBoundingClientRect();
        const min = control.min === '' ? 0 : Number(control.min);
        const max = control.max === '' ? 100 : Number(control.max);
        // thumb centers travel half a thumb width in from the track ends
        const fraction = (control.valueAsNumber - min) / (max - min);
        const thumbX = rect.left + THUMB_WIDTH / 2 + fraction * (rect.width - THUMB_WIDTH);
        return Math.abs(clientX - thumbX) > THUMB_WIDTH / 2 + HIT_SLOP;
    };
    control.addEventListener('pointerdown', (event)=>{
        if (event.pointerType === 'mouse') return; // handled at mousedown below
        offThumb = pressOffThumb(event.clientX);
        snapshot = control.valueAsNumber;
    });
    control.addEventListener('mousedown', (event)=>{
        if (pressOffThumb(event.clientX)) event.preventDefault();
    });
    // Blink fires input as it moves the thumb to the press point; restoring
    // on each one keeps the thumb (and the readout) put for the gesture
    control.addEventListener('input', ()=>{
        if (offThumb) restore();
    });
    const end = ()=>{
        if (offThumb) restore();
        offThumb = false;
        snapshot = null;
    };
    control.addEventListener('pointerup', end);
    control.addEventListener('pointercancel', end);
}
/**
 * Bind a range input to a numeric value: initialise the control from get(),
 * and on change call set() (which persists and updates the live config).
 */ function $d37efb89f06c4220$export$4fc2d638ed78045a(id, get, set) {
    const control = document.getElementById(id);
    if (control) {
        control.addEventListener('change', (event)=>{
            set(event.target.valueAsNumber);
        });
        control.valueAsNumber = get();
        // sync the <output> displays wired up via the inline oninput handlers
        control.dispatchEvent(new Event('input'));
        $d37efb89f06c4220$var$guardRangeTrackPress(control);
    }
}
/**
 * Bind a checkbox control to a boolean value: initialise the control from
 * get(), and on click call set().
 */ function $d37efb89f06c4220$export$913f1d0f2a072d8c(id, get, set) {
    const control = document.getElementById(id);
    if (control) {
        control.addEventListener('click', (event)=>{
            set(event.target.checked);
        });
        control.checked = get();
    }
}
/**
 * Make a big bpm readout editable: click (or Enter/Space while focused) to
 * swap it for a number input; Enter/blur commits, Escape cancels.
 * Committing updates the slider and fires its change event, reusing the
 * bindNumericControl path to persist and update settings.
 */ function $d37efb89f06c4220$export$e0a99e6a99ef25a0(outputId, inputId, sliderId) {
    const output = document.getElementById(outputId);
    const input = document.getElementById(inputId);
    const slider = document.getElementById(sliderId);
    if (!output || !input || !slider) return;
    let cancelled = false;
    const startEditing = ()=>{
        input.value = output.value;
        output.hidden = true;
        input.hidden = false;
        input.focus();
        input.select();
    };
    const stopEditing = ()=>{
        input.hidden = true;
        output.hidden = false;
    };
    const commit = ()=>{
        if (!isNaN(input.valueAsNumber)) {
            const min = Number(slider.min);
            const max = Number(slider.max);
            const value = Math.min(Math.max(Math.round(input.valueAsNumber), min), max);
            output.value = '' + value;
            slider.valueAsNumber = value;
            slider.dispatchEvent(new Event('change'));
        }
        stopEditing();
    };
    output.addEventListener('click', startEditing);
    output.addEventListener('keydown', (event)=>{
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            startEditing();
        }
    });
    input.addEventListener('keydown', (event)=>{
        if (event.key === 'Enter') commit();
        else if (event.key === 'Escape') {
            cancelled = true;
            stopEditing();
        }
    });
    input.addEventListener('blur', ()=>{
        // Escape hides the input, which fires blur; don't commit that
        if (cancelled) {
            cancelled = false;
            return;
        }
        commit();
    });
}



/**
 * Make a node according to the specified options
 * @param currentDepth
 * @param weight default 1
 * @param isLeaf a function used to determine whether this node should be a leaf or not
 * @param divideBy how many children to create, if isLeaf === false
 * @param isTuplet is this node being created as part of a tuplet?
 */ function $efdf968da99467e3$export$270e7ba5936d3c48(currentDepth = 0, weight = 1, isLeaf, divideBy, isTuplet = false) {
    if (isLeaf(currentDepth, isTuplet)) return {
        kind: 'leaf',
        weight: weight
    };
    const subdivision = divideBy(currentDepth, weight, isTuplet);
    const children = [];
    for(let i = 0; i < subdivision; i++)children.push($efdf968da99467e3$export$270e7ba5936d3c48(currentDepth + 1, 1, isLeaf, divideBy, subdivision > 2));
    return {
        kind: 'node',
        weight: weight,
        children: children
    };
}
/**
 * The sum of the `value` weights of a node's children.
 * Used to derive each child's proportional share of the parent's duration.
 *
 * @throws if the node has no children (the result would be 0 and would make
 *         any derived proportional duration NaN).
 */ function $efdf968da99467e3$export$77af52294bc119da(node) {
    if (node.children.length === 0) throw new Error('RhythmNode must have at least one child');
    return node.children.reduce((sum, child)=>sum + child.weight, 0);
}
/**
 * Flattens a RhythmTree to RhythmEvents from RhythmLeaf nodes.
 *
 * Each leaf's duration is derived from its position in the weight tree and the
 * root's absolute duration. The root's own `value` is ignored. The unit of the
 * output matches the unit of `rootDuration` (seconds, beats, bars, etc.) - the
 * tree itself is unit-agnostic.
 *
 * Output is rhythm-only: it carries just `duration`. Domain concerns such as
 * rests, pitch, or articulation are a separate concern.
 *
 * @throws if a RhythmNode with no children is encountered during traversal.
 */ function $efdf968da99467e3$export$bffa455ba8c619a6(node, duration) {
    if (node.kind === 'leaf') return [
        {
            duration: duration
        }
    ];
    const weight = $efdf968da99467e3$export$77af52294bc119da(node);
    return node.children.flatMap((child)=>$efdf968da99467e3$export$bffa455ba8c619a6(child, duration * (child.weight / weight)));
}





// Partially determines possible subdivisions.
const $2b01753db8423e69$var$MAX_DEPTH = 4;
const $2b01753db8423e69$var$MAX_GRANULARITY = 100;
/**
 * Intention is to create a bar from a single node. Each subdivision depth should
 * be managed separately in terms of probability, so that we can ensure that subdivisions
 * like whole not triplets are less likely than quarter note triplets, for example.
 *
 * Builds a tree that allows for any number of beats per bar as the root's children.
 * Groups odd numbers into weights of 2s and 1s, which can then be subdivided.
 */ class $2b01753db8423e69$export$b135b2512b192788 {
    config;
    minLeafDepth;
    isLeaf = (currentDepth, isTuplet)=>{
        // An index into each granularity range, e.g. 0 -> 1 - 25, 1 -> 26 - 50, etc.
        // Basically divides the full granularity range (hardcoded to 100 atm), by the MAX_DEPTH, and
        // then uses the result to convert the current granularity setting into a maxDepth.
        // Then roughly converts the granularity slider into a probability, which translates
        // into how much a beat might be subdivided
        const settingsMaxDepth = (0, $910fcc7bce5be3ba$export$215181f5b8457fa2)(1, $2b01753db8423e69$var$MAX_GRANULARITY, $2b01753db8423e69$var$MAX_DEPTH, this.config.granularity);
        const randomisedMaxDepth = (0, $910fcc7bce5be3ba$export$b4da2a2d574a34b7)(0, settingsMaxDepth, 50) + this.minLeafDepth;
        // Effectively, might return a leaf even when not at max depth.
        // And don't allow nested tuplets.
        return currentDepth >= randomisedMaxDepth || isTuplet;
    };
    subdivideBy = (currentDepth)=>{
        // A 100% tuplet bias doesn't work because it means the top level always gets
        // subdivided and then nothing below it (we are not allowing nested tuplets in this algo).
        // Instead, use a combination of the tuplet bias, current depth and granularity to weight more
        // towards tuplets in smaller subdivisions, i.e. lower in the tree. If we are at max
        // tuplet bias, only the deepest level should all be tupletized.
        const settingsMaxDepth = (0, $910fcc7bce5be3ba$export$215181f5b8457fa2)(1, $2b01753db8423e69$var$MAX_GRANULARITY, $2b01753db8423e69$var$MAX_DEPTH, this.config.granularity) + this.minLeafDepth;
        // Set the bias based on where the currentDepth is in relation
        // to the settingsMaxDepth. Shallower -> less bias, Deeper -> more bias
        const bias = this.config.tupletBias / settingsMaxDepth * (currentDepth + 1);
        return (0, $910fcc7bce5be3ba$export$b4da2a2d574a34b7)(2, 3, bias);
    };
    constructor(config){
        this.config = config;
        this.minLeafDepth = config.minLeafDepth ?? 1;
    }
    // create the top level bar structure, before delegating to the more generic createNode function
    generate() {
        // Group beatsPerBar into weights of 2s and 1s. This allows for
        // subdivisions of 2 beats at a time, which then allows for half note triplets.
        // each group represents either 1 or 2 'periods' (beats)
        const groups = this.createWeightGroups(this.config.beatsPerBar);
        const children = groups.map((weightGroup)=>{
            // Effectively balances the depth of the tree by allowing nodes with more weight (2s)
            // to be subdivided when 1s won't get subdivided. A bit hacky, but it works.
            // An unwanted side effect is that the tree may not go deep enough for low beatsPerMinute settings.
            const newDepth = weightGroup === 1 ? this.minLeafDepth : Math.max(0, this.minLeafDepth - 1);
            return (0, $efdf968da99467e3$export$270e7ba5936d3c48)(newDepth, weightGroup, this.isLeaf, this.subdivideBy);
        });
        return {
            kind: 'node',
            weight: 1,
            children: children
        };
    }
    /**
     * Takes total weight and returns an array which groups total weight
     * into 2s and 1s, and effectively shuffles the result. For small values (1, 2, 3),
     * returns groups with only 1s.
     * @param totalWeight
     * @private
     */ createWeightGroups(totalWeight) {
        if (totalWeight <= 0) return [];
        switch(totalWeight){
            case 1:
                return [
                    1
                ];
            case 2:
                return [
                    1,
                    1
                ];
            case 3:
                return [
                    1,
                    1,
                    1
                ];
            default:
                {
                    const twos = Math.floor(totalWeight / 2);
                    const hasRemainder = totalWeight % 2 !== 0;
                    const groups = Array(twos).fill(2);
                    if (hasRemainder) {
                        const randomIndex = (0, $910fcc7bce5be3ba$export$b4da2a2d574a34b7)(0, groups.length);
                        groups.splice(randomIndex, 0, 1);
                    }
                    return groups;
                }
        }
    }
}


/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */ /* global Reflect, Promise, SuppressedError, Symbol, Iterator */ var $8e874a2fb001c377$var$extendStatics = function(d, b) {
    $8e874a2fb001c377$var$extendStatics = Object.setPrototypeOf || ({
        __proto__: []
    }) instanceof Array && function(d, b) {
        d.__proto__ = b;
    } || function(d, b) {
        for(var p in b)if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p];
    };
    return $8e874a2fb001c377$var$extendStatics(d, b);
};
function $8e874a2fb001c377$export$a8ba968b8961cb8a(d, b) {
    if (typeof b !== "function" && b !== null) throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    $8e874a2fb001c377$var$extendStatics(d, b);
    function __() {
        this.constructor = d;
    }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}
var $8e874a2fb001c377$export$18ce0697a983be9b = function() {
    $8e874a2fb001c377$export$18ce0697a983be9b = Object.assign || function __assign(t) {
        for(var s, i = 1, n = arguments.length; i < n; i++){
            s = arguments[i];
            for(var p in s)if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return $8e874a2fb001c377$export$18ce0697a983be9b.apply(this, arguments);
};
function $8e874a2fb001c377$export$3c9a16f847548506(s, e) {
    var t = {};
    for(var p in s)if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function") {
        for(var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++)if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
    }
    return t;
}
function $8e874a2fb001c377$export$29e00dfd3077644b(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function $8e874a2fb001c377$export$d5ad3fd78186038f(paramIndex, decorator) {
    return function(target, key) {
        decorator(target, key, paramIndex);
    };
}
function $8e874a2fb001c377$export$3a84e1ae4e97e9b0(ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) {
        if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected");
        return f;
    }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for(var i = decorators.length - 1; i >= 0; i--){
        var context = {};
        for(var p in contextIn)context[p] = p === "access" ? {} : contextIn[p];
        for(var p in contextIn.access)context.access[p] = contextIn.access[p];
        context.addInitializer = function(f) {
            if (done) throw new TypeError("Cannot add initializers after decoration has completed");
            extraInitializers.push(accept(f || null));
        };
        var result = (0, decorators[i])(kind === "accessor" ? {
            get: descriptor.get,
            set: descriptor.set
        } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        } else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
}
function $8e874a2fb001c377$export$d831c04e792af3d(thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for(var i = 0; i < initializers.length; i++)value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    return useValue ? value : void 0;
}
function $8e874a2fb001c377$export$6a2a36740a146cb8(x) {
    return typeof x === "symbol" ? x : "".concat(x);
}
function $8e874a2fb001c377$export$d1a06452d3489bc7(f, name, prefix) {
    if (typeof name === "symbol") name = name.description ? "[".concat(name.description, "]") : "";
    return Object.defineProperty(f, "name", {
        configurable: true,
        value: prefix ? "".concat(prefix, " ", name) : name
    });
}
function $8e874a2fb001c377$export$f1db080c865becb9(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}
function $8e874a2fb001c377$export$1050f835b63b671e(thisArg, _arguments, P, generator) {
    function adopt(value) {
        return value instanceof P ? value : new P(function(resolve) {
            resolve(value);
        });
    }
    return new (P || (P = Promise))(function(resolve, reject) {
        function fulfilled(value) {
            try {
                step(generator.next(value));
            } catch (e) {
                reject(e);
            }
        }
        function rejected(value) {
            try {
                step(generator["throw"](value));
            } catch (e) {
                reject(e);
            }
        }
        function step(result) {
            result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
        }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}
function $8e874a2fb001c377$export$67ebef60e6f28a6(thisArg, body) {
    var _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(g && (g = 0, op[0] && (_ = 0)), _)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var $8e874a2fb001c377$export$45d3717a4c69092e = Object.create ? function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) desc = {
        enumerable: true,
        get: function() {
            return m[k];
        }
    };
    Object.defineProperty(o, k2, desc);
} : function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
};
function $8e874a2fb001c377$export$f33643c0debef087(m, o) {
    for(var p in m)if (p !== "default" && !Object.prototype.hasOwnProperty.call(o, p)) $8e874a2fb001c377$export$45d3717a4c69092e(o, m, p);
}
function $8e874a2fb001c377$export$19a8beecd37a4c45(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function() {
            if (o && i >= o.length) o = void 0;
            return {
                value: o && o[i++],
                done: !o
            };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
}
function $8e874a2fb001c377$export$8d051b38c9118094(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while((n === void 0 || n-- > 0) && !(r = i.next()).done)ar.push(r.value);
    } catch (error) {
        e = {
            error: error
        };
    } finally{
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        } finally{
            if (e) throw e.error;
        }
    }
    return ar;
}
function $8e874a2fb001c377$export$afc72e2116322959() {
    for(var ar = [], i = 0; i < arguments.length; i++)ar = ar.concat($8e874a2fb001c377$export$8d051b38c9118094(arguments[i]));
    return ar;
}
function $8e874a2fb001c377$export$6388937ca91ccae8() {
    for(var s = 0, i = 0, il = arguments.length; i < il; i++)s += arguments[i].length;
    for(var r = Array(s), k = 0, i = 0; i < il; i++)for(var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)r[k] = a[j];
    return r;
}
function $8e874a2fb001c377$export$1216008129fb82ed(to, from, pack) {
    if (pack || arguments.length === 2) {
        for(var i = 0, l = from.length, ar; i < l; i++)if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}
function $8e874a2fb001c377$export$10c90e4f7922046c(v) {
    return this instanceof $8e874a2fb001c377$export$10c90e4f7922046c ? (this.v = v, this) : new $8e874a2fb001c377$export$10c90e4f7922046c(v);
}
function $8e874a2fb001c377$export$e427f37a30a4de9b(thisArg, _arguments, generator) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var g = generator.apply(thisArg, _arguments || []), i, q = [];
    return i = Object.create((typeof AsyncIterator === "function" ? AsyncIterator : Object).prototype), verb("next"), verb("throw"), verb("return", awaitReturn), i[Symbol.asyncIterator] = function() {
        return this;
    }, i;
    function awaitReturn(f) {
        return function(v) {
            return Promise.resolve(v).then(f, reject);
        };
    }
    function verb(n, f) {
        if (g[n]) {
            i[n] = function(v) {
                return new Promise(function(a, b) {
                    q.push([
                        n,
                        v,
                        a,
                        b
                    ]) > 1 || resume(n, v);
                });
            };
            if (f) i[n] = f(i[n]);
        }
    }
    function resume(n, v) {
        try {
            step(g[n](v));
        } catch (e) {
            settle(q[0][3], e);
        }
    }
    function step(r) {
        r.value instanceof $8e874a2fb001c377$export$10c90e4f7922046c ? Promise.resolve(r.value.v).then(fulfill, reject) : settle(q[0][2], r);
    }
    function fulfill(value) {
        resume("next", value);
    }
    function reject(value) {
        resume("throw", value);
    }
    function settle(f, v) {
        if (f(v), q.shift(), q.length) resume(q[0][0], q[0][1]);
    }
}
function $8e874a2fb001c377$export$bbd80228419bb833(o) {
    var i, p;
    return i = {}, verb("next"), verb("throw", function(e) {
        throw e;
    }), verb("return"), i[Symbol.iterator] = function() {
        return this;
    }, i;
    function verb(n, f) {
        i[n] = o[n] ? function(v) {
            return (p = !p) ? {
                value: $8e874a2fb001c377$export$10c90e4f7922046c(o[n](v)),
                done: false
            } : f ? f(v) : v;
        } : f;
    }
}
function $8e874a2fb001c377$export$e3b29a3d6162315f(o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof $8e874a2fb001c377$export$19a8beecd37a4c45 === "function" ? $8e874a2fb001c377$export$19a8beecd37a4c45(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function() {
        return this;
    }, i);
    function verb(n) {
        i[n] = o[n] && function(v) {
            return new Promise(function(resolve, reject) {
                v = o[n](v), settle(resolve, reject, v.done, v.value);
            });
        };
    }
    function settle(resolve, reject, d, v) {
        Promise.resolve(v).then(function(v) {
            resolve({
                value: v,
                done: d
            });
        }, reject);
    }
}
function $8e874a2fb001c377$export$4fb47efe1390b86f(cooked, raw) {
    if (Object.defineProperty) Object.defineProperty(cooked, "raw", {
        value: raw
    });
    else cooked.raw = raw;
    return cooked;
}
var $8e874a2fb001c377$var$__setModuleDefault = Object.create ? function(o, v) {
    Object.defineProperty(o, "default", {
        enumerable: true,
        value: v
    });
} : function(o, v) {
    o["default"] = v;
};
var $8e874a2fb001c377$var$ownKeys = function(o) {
    $8e874a2fb001c377$var$ownKeys = Object.getOwnPropertyNames || function(o) {
        var ar = [];
        for(var k in o)if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
        return ar;
    };
    return $8e874a2fb001c377$var$ownKeys(o);
};
function $8e874a2fb001c377$export$c21735bcef00d192(mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) {
        for(var k = $8e874a2fb001c377$var$ownKeys(mod), i = 0; i < k.length; i++)if (k[i] !== "default") $8e874a2fb001c377$export$45d3717a4c69092e(result, mod, k[i]);
    }
    $8e874a2fb001c377$var$__setModuleDefault(result, mod);
    return result;
}
function $8e874a2fb001c377$export$da59b14a69baef04(mod) {
    return mod && mod.__esModule ? mod : {
        default: mod
    };
}
function $8e874a2fb001c377$export$d5dcaf168c640c35(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}
function $8e874a2fb001c377$export$d40a35129aaff81f(receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value), value;
}
function $8e874a2fb001c377$export$81fdc39f203e4e04(state, receiver) {
    if (receiver === null || typeof receiver !== "object" && typeof receiver !== "function") throw new TypeError("Cannot use 'in' operator on non-object");
    return typeof state === "function" ? receiver === state : state.has(receiver);
}
function $8e874a2fb001c377$export$88ac25d8e944e405(env, value, async) {
    if (value !== null && value !== void 0) {
        if (typeof value !== "object" && typeof value !== "function") throw new TypeError("Object expected.");
        var dispose, inner;
        if (async) {
            if (!Symbol.asyncDispose) throw new TypeError("Symbol.asyncDispose is not defined.");
            dispose = value[Symbol.asyncDispose];
        }
        if (dispose === void 0) {
            if (!Symbol.dispose) throw new TypeError("Symbol.dispose is not defined.");
            dispose = value[Symbol.dispose];
            if (async) inner = dispose;
        }
        if (typeof dispose !== "function") throw new TypeError("Object not disposable.");
        if (inner) dispose = function() {
            try {
                inner.call(this);
            } catch (e) {
                return Promise.reject(e);
            }
        };
        env.stack.push({
            value: value,
            dispose: dispose,
            async: async
        });
    } else if (async) env.stack.push({
        async: true
    });
    return value;
}
var $8e874a2fb001c377$var$_SuppressedError = typeof SuppressedError === "function" ? SuppressedError : function(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};
function $8e874a2fb001c377$export$8f076105dc360e92(env) {
    function fail(e) {
        env.error = env.hasError ? new $8e874a2fb001c377$var$_SuppressedError(e, env.error, "An error was suppressed during disposal.") : e;
        env.hasError = true;
    }
    var r, s = 0;
    function next() {
        while(r = env.stack.pop())try {
            if (!r.async && s === 1) return s = 0, env.stack.push(r), Promise.resolve().then(next);
            if (r.dispose) {
                var result = r.dispose.call(r.value);
                if (r.async) return s |= 2, Promise.resolve(result).then(next, function(e) {
                    fail(e);
                    return next();
                });
            } else s |= 1;
        } catch (e) {
            fail(e);
        }
        if (s === 1) return env.hasError ? Promise.reject(env.error) : Promise.resolve();
        if (env.hasError) throw env.error;
    }
    return next();
}
function $8e874a2fb001c377$export$889dfb5d17574b0b(path, preserveJsx) {
    if (typeof path === "string" && /^\.\.?\//.test(path)) return path.replace(/\.(tsx)$|((?:\.d)?)((?:\.[^./]+?)?)\.([cm]?)ts$/i, function(m, tsx, d, ext, cm) {
        return tsx ? preserveJsx ? ".jsx" : ".js" : d && (!ext || !cm) ? m : d + ext + "." + cm.toLowerCase() + "js";
    });
    return path;
}
var $8e874a2fb001c377$export$2e2bcd8739ae039 = {
    __extends: $8e874a2fb001c377$export$a8ba968b8961cb8a,
    __assign: $8e874a2fb001c377$export$18ce0697a983be9b,
    __rest: $8e874a2fb001c377$export$3c9a16f847548506,
    __decorate: $8e874a2fb001c377$export$29e00dfd3077644b,
    __param: $8e874a2fb001c377$export$d5ad3fd78186038f,
    __esDecorate: $8e874a2fb001c377$export$3a84e1ae4e97e9b0,
    __runInitializers: $8e874a2fb001c377$export$d831c04e792af3d,
    __propKey: $8e874a2fb001c377$export$6a2a36740a146cb8,
    __setFunctionName: $8e874a2fb001c377$export$d1a06452d3489bc7,
    __metadata: $8e874a2fb001c377$export$f1db080c865becb9,
    __awaiter: $8e874a2fb001c377$export$1050f835b63b671e,
    __generator: $8e874a2fb001c377$export$67ebef60e6f28a6,
    __createBinding: $8e874a2fb001c377$export$45d3717a4c69092e,
    __exportStar: $8e874a2fb001c377$export$f33643c0debef087,
    __values: $8e874a2fb001c377$export$19a8beecd37a4c45,
    __read: $8e874a2fb001c377$export$8d051b38c9118094,
    __spread: $8e874a2fb001c377$export$afc72e2116322959,
    __spreadArrays: $8e874a2fb001c377$export$6388937ca91ccae8,
    __spreadArray: $8e874a2fb001c377$export$1216008129fb82ed,
    __await: $8e874a2fb001c377$export$10c90e4f7922046c,
    __asyncGenerator: $8e874a2fb001c377$export$e427f37a30a4de9b,
    __asyncDelegator: $8e874a2fb001c377$export$bbd80228419bb833,
    __asyncValues: $8e874a2fb001c377$export$e3b29a3d6162315f,
    __makeTemplateObject: $8e874a2fb001c377$export$4fb47efe1390b86f,
    __importStar: $8e874a2fb001c377$export$c21735bcef00d192,
    __importDefault: $8e874a2fb001c377$export$da59b14a69baef04,
    __classPrivateFieldGet: $8e874a2fb001c377$export$d5dcaf168c640c35,
    __classPrivateFieldSet: $8e874a2fb001c377$export$d40a35129aaff81f,
    __classPrivateFieldIn: $8e874a2fb001c377$export$81fdc39f203e4e04,
    __addDisposableResource: $8e874a2fb001c377$export$88ac25d8e944e405,
    __disposeResources: $8e874a2fb001c377$export$8f076105dc360e92,
    __rewriteRelativeImportExtension: $8e874a2fb001c377$export$889dfb5d17574b0b
};



function $9f4cd17d3bfb739b$export$f6e2535fb5126e54(value) {
    return typeof value === 'function';
}




function $df5c9cb6183aeff9$export$128a15b65d1b6041(createImpl) {
    var _super = function(instance) {
        Error.call(instance);
        instance.stack = new Error().stack;
    };
    var ctorFunc = createImpl(_super);
    ctorFunc.prototype = Object.create(Error.prototype);
    ctorFunc.prototype.constructor = ctorFunc;
    return ctorFunc;
}


var $7e50a408673c5151$export$c9648b76fd580c34 = (0, $df5c9cb6183aeff9$export$128a15b65d1b6041)(function(_super) {
    return function UnsubscriptionErrorImpl(errors) {
        _super(this);
        this.message = errors ? errors.length + " errors occurred during unsubscription:\n" + errors.map(function(err, i) {
            return i + 1 + ") " + err.toString();
        }).join('\n  ') : '';
        this.name = 'UnsubscriptionError';
        this.errors = errors;
    };
});


function $a53e47be1b60c3c3$export$dae3f38077fc36c0(arr, item) {
    if (arr) {
        var index = arr.indexOf(item);
        0 <= index && arr.splice(index, 1);
    }
}


var $b1d1a261220cb194$export$f55210826850c514 = function() {
    function Subscription(initialTeardown) {
        this.initialTeardown = initialTeardown;
        this.closed = false;
        this._parentage = null;
        this._finalizers = null;
    }
    Subscription.prototype.unsubscribe = function() {
        var e_1, _a, e_2, _b;
        var errors;
        if (!this.closed) {
            this.closed = true;
            var _parentage = this._parentage;
            if (_parentage) {
                this._parentage = null;
                if (Array.isArray(_parentage)) try {
                    for(var _parentage_1 = (0, $8e874a2fb001c377$export$19a8beecd37a4c45)(_parentage), _parentage_1_1 = _parentage_1.next(); !_parentage_1_1.done; _parentage_1_1 = _parentage_1.next()){
                        var parent_1 = _parentage_1_1.value;
                        parent_1.remove(this);
                    }
                } catch (e_1_1) {
                    e_1 = {
                        error: e_1_1
                    };
                } finally{
                    try {
                        if (_parentage_1_1 && !_parentage_1_1.done && (_a = _parentage_1.return)) _a.call(_parentage_1);
                    } finally{
                        if (e_1) throw e_1.error;
                    }
                }
                else _parentage.remove(this);
            }
            var initialFinalizer = this.initialTeardown;
            if ((0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(initialFinalizer)) try {
                initialFinalizer();
            } catch (e) {
                errors = e instanceof (0, $7e50a408673c5151$export$c9648b76fd580c34) ? e.errors : [
                    e
                ];
            }
            var _finalizers = this._finalizers;
            if (_finalizers) {
                this._finalizers = null;
                try {
                    for(var _finalizers_1 = (0, $8e874a2fb001c377$export$19a8beecd37a4c45)(_finalizers), _finalizers_1_1 = _finalizers_1.next(); !_finalizers_1_1.done; _finalizers_1_1 = _finalizers_1.next()){
                        var finalizer = _finalizers_1_1.value;
                        try {
                            $b1d1a261220cb194$var$execFinalizer(finalizer);
                        } catch (err) {
                            errors = errors !== null && errors !== void 0 ? errors : [];
                            if (err instanceof (0, $7e50a408673c5151$export$c9648b76fd580c34)) errors = (0, $8e874a2fb001c377$export$1216008129fb82ed)((0, $8e874a2fb001c377$export$1216008129fb82ed)([], (0, $8e874a2fb001c377$export$8d051b38c9118094)(errors)), (0, $8e874a2fb001c377$export$8d051b38c9118094)(err.errors));
                            else errors.push(err);
                        }
                    }
                } catch (e_2_1) {
                    e_2 = {
                        error: e_2_1
                    };
                } finally{
                    try {
                        if (_finalizers_1_1 && !_finalizers_1_1.done && (_b = _finalizers_1.return)) _b.call(_finalizers_1);
                    } finally{
                        if (e_2) throw e_2.error;
                    }
                }
            }
            if (errors) throw new (0, $7e50a408673c5151$export$c9648b76fd580c34)(errors);
        }
    };
    Subscription.prototype.add = function(teardown) {
        var _a;
        if (teardown && teardown !== this) {
            if (this.closed) $b1d1a261220cb194$var$execFinalizer(teardown);
            else {
                if (teardown instanceof Subscription) {
                    if (teardown.closed || teardown._hasParent(this)) return;
                    teardown._addParent(this);
                }
                (this._finalizers = (_a = this._finalizers) !== null && _a !== void 0 ? _a : []).push(teardown);
            }
        }
    };
    Subscription.prototype._hasParent = function(parent) {
        var _parentage = this._parentage;
        return _parentage === parent || Array.isArray(_parentage) && _parentage.includes(parent);
    };
    Subscription.prototype._addParent = function(parent) {
        var _parentage = this._parentage;
        this._parentage = Array.isArray(_parentage) ? (_parentage.push(parent), _parentage) : _parentage ? [
            _parentage,
            parent
        ] : parent;
    };
    Subscription.prototype._removeParent = function(parent) {
        var _parentage = this._parentage;
        if (_parentage === parent) this._parentage = null;
        else if (Array.isArray(_parentage)) (0, $a53e47be1b60c3c3$export$dae3f38077fc36c0)(_parentage, parent);
    };
    Subscription.prototype.remove = function(teardown) {
        var _finalizers = this._finalizers;
        _finalizers && (0, $a53e47be1b60c3c3$export$dae3f38077fc36c0)(_finalizers, teardown);
        if (teardown instanceof Subscription) teardown._removeParent(this);
    };
    Subscription.EMPTY = function() {
        var empty = new Subscription();
        empty.closed = true;
        return empty;
    }();
    return Subscription;
}();
var $b1d1a261220cb194$export$610f9c3ca1a38dd8 = $b1d1a261220cb194$export$f55210826850c514.EMPTY;
function $b1d1a261220cb194$export$4f9221cbada3c4fc(value) {
    return value instanceof $b1d1a261220cb194$export$f55210826850c514 || value && 'closed' in value && (0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(value.remove) && (0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(value.add) && (0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(value.unsubscribe);
}
function $b1d1a261220cb194$var$execFinalizer(finalizer) {
    if ((0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(finalizer)) finalizer();
    else finalizer.unsubscribe();
}


var $130e6ab1a4f4ab41$export$e506a1d27d1eaa20 = {
    onUnhandledError: null,
    onStoppedNotification: null,
    Promise: undefined,
    useDeprecatedSynchronousErrorHandling: false,
    useDeprecatedNextContext: false
};




var $54e99c791bb8207c$export$365aa6bd3c788e3d = {
    setTimeout: function(handler, timeout) {
        var args = [];
        for(var _i = 2; _i < arguments.length; _i++)args[_i - 2] = arguments[_i];
        var delegate = $54e99c791bb8207c$export$365aa6bd3c788e3d.delegate;
        if (delegate === null || delegate === void 0 ? void 0 : delegate.setTimeout) return delegate.setTimeout.apply(delegate, (0, $8e874a2fb001c377$export$1216008129fb82ed)([
            handler,
            timeout
        ], (0, $8e874a2fb001c377$export$8d051b38c9118094)(args)));
        return setTimeout.apply(void 0, (0, $8e874a2fb001c377$export$1216008129fb82ed)([
            handler,
            timeout
        ], (0, $8e874a2fb001c377$export$8d051b38c9118094)(args)));
    },
    clearTimeout: function(handle) {
        var delegate = $54e99c791bb8207c$export$365aa6bd3c788e3d.delegate;
        return ((delegate === null || delegate === void 0 ? void 0 : delegate.clearTimeout) || clearTimeout)(handle);
    },
    delegate: undefined
};


function $0b985c2ba288a978$export$ebf645db02d3e99f(err) {
    (0, $54e99c791bb8207c$export$365aa6bd3c788e3d).setTimeout(function() {
        var onUnhandledError = (0, $130e6ab1a4f4ab41$export$e506a1d27d1eaa20).onUnhandledError;
        if (onUnhandledError) onUnhandledError(err);
        else throw err;
    });
}


function $09d0e01f28fdde08$export$8793edee2d425525() {}


var $fdc189fd52d09f69$export$b7f97edb34bc1f87 = function() {
    return $fdc189fd52d09f69$export$9c8f6e8d39c60cf3('C', undefined, undefined);
}();
function $fdc189fd52d09f69$export$dd472c3d8fe5a85a(error) {
    return $fdc189fd52d09f69$export$9c8f6e8d39c60cf3('E', undefined, error);
}
function $fdc189fd52d09f69$export$ff9346b0d1d30313(value) {
    return $fdc189fd52d09f69$export$9c8f6e8d39c60cf3('N', value, undefined);
}
function $fdc189fd52d09f69$export$9c8f6e8d39c60cf3(kind, value, error) {
    return {
        kind: kind,
        value: value,
        error: error
    };
}




var $db83674f177e2fa2$var$context = null;
function $db83674f177e2fa2$export$d974811edc77eafa(cb) {
    if ((0, $130e6ab1a4f4ab41$export$e506a1d27d1eaa20).useDeprecatedSynchronousErrorHandling) {
        var isRoot = !$db83674f177e2fa2$var$context;
        if (isRoot) $db83674f177e2fa2$var$context = {
            errorThrown: false,
            error: null
        };
        cb();
        if (isRoot) {
            var _a = $db83674f177e2fa2$var$context, errorThrown = _a.errorThrown, error = _a.error;
            $db83674f177e2fa2$var$context = null;
            if (errorThrown) throw error;
        }
    } else cb();
}
function $db83674f177e2fa2$export$3e205fbb5df021e0(err) {
    if ((0, $130e6ab1a4f4ab41$export$e506a1d27d1eaa20).useDeprecatedSynchronousErrorHandling && $db83674f177e2fa2$var$context) {
        $db83674f177e2fa2$var$context.errorThrown = true;
        $db83674f177e2fa2$var$context.error = err;
    }
}


var $a21822406e53a1a5$export$60959659b2c22881 = function(_super) {
    (0, $8e874a2fb001c377$export$a8ba968b8961cb8a)(Subscriber, _super);
    function Subscriber(destination) {
        var _this = _super.call(this) || this;
        _this.isStopped = false;
        if (destination) {
            _this.destination = destination;
            if ((0, $b1d1a261220cb194$export$4f9221cbada3c4fc)(destination)) destination.add(_this);
        } else _this.destination = $a21822406e53a1a5$export$88d395d20619a0af;
        return _this;
    }
    Subscriber.create = function(next, error, complete) {
        return new $a21822406e53a1a5$export$94eeb6a328cab6c7(next, error, complete);
    };
    Subscriber.prototype.next = function(value) {
        if (this.isStopped) $a21822406e53a1a5$var$handleStoppedNotification((0, $fdc189fd52d09f69$export$ff9346b0d1d30313)(value), this);
        else this._next(value);
    };
    Subscriber.prototype.error = function(err) {
        if (this.isStopped) $a21822406e53a1a5$var$handleStoppedNotification((0, $fdc189fd52d09f69$export$dd472c3d8fe5a85a)(err), this);
        else {
            this.isStopped = true;
            this._error(err);
        }
    };
    Subscriber.prototype.complete = function() {
        if (this.isStopped) $a21822406e53a1a5$var$handleStoppedNotification((0, $fdc189fd52d09f69$export$b7f97edb34bc1f87), this);
        else {
            this.isStopped = true;
            this._complete();
        }
    };
    Subscriber.prototype.unsubscribe = function() {
        if (!this.closed) {
            this.isStopped = true;
            _super.prototype.unsubscribe.call(this);
            this.destination = null;
        }
    };
    Subscriber.prototype._next = function(value) {
        this.destination.next(value);
    };
    Subscriber.prototype._error = function(err) {
        try {
            this.destination.error(err);
        } finally{
            this.unsubscribe();
        }
    };
    Subscriber.prototype._complete = function() {
        try {
            this.destination.complete();
        } finally{
            this.unsubscribe();
        }
    };
    return Subscriber;
}((0, $b1d1a261220cb194$export$f55210826850c514));
var $a21822406e53a1a5$var$_bind = Function.prototype.bind;
function $a21822406e53a1a5$var$bind(fn, thisArg) {
    return $a21822406e53a1a5$var$_bind.call(fn, thisArg);
}
var $a21822406e53a1a5$var$ConsumerObserver = function() {
    function ConsumerObserver(partialObserver) {
        this.partialObserver = partialObserver;
    }
    ConsumerObserver.prototype.next = function(value) {
        var partialObserver = this.partialObserver;
        if (partialObserver.next) try {
            partialObserver.next(value);
        } catch (error) {
            $a21822406e53a1a5$var$handleUnhandledError(error);
        }
    };
    ConsumerObserver.prototype.error = function(err) {
        var partialObserver = this.partialObserver;
        if (partialObserver.error) try {
            partialObserver.error(err);
        } catch (error) {
            $a21822406e53a1a5$var$handleUnhandledError(error);
        }
        else $a21822406e53a1a5$var$handleUnhandledError(err);
    };
    ConsumerObserver.prototype.complete = function() {
        var partialObserver = this.partialObserver;
        if (partialObserver.complete) try {
            partialObserver.complete();
        } catch (error) {
            $a21822406e53a1a5$var$handleUnhandledError(error);
        }
    };
    return ConsumerObserver;
}();
var $a21822406e53a1a5$export$94eeb6a328cab6c7 = function(_super) {
    (0, $8e874a2fb001c377$export$a8ba968b8961cb8a)(SafeSubscriber, _super);
    function SafeSubscriber(observerOrNext, error, complete) {
        var _this = _super.call(this) || this;
        var partialObserver;
        if ((0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(observerOrNext) || !observerOrNext) partialObserver = {
            next: observerOrNext !== null && observerOrNext !== void 0 ? observerOrNext : undefined,
            error: error !== null && error !== void 0 ? error : undefined,
            complete: complete !== null && complete !== void 0 ? complete : undefined
        };
        else {
            var context_1;
            if (_this && (0, $130e6ab1a4f4ab41$export$e506a1d27d1eaa20).useDeprecatedNextContext) {
                context_1 = Object.create(observerOrNext);
                context_1.unsubscribe = function() {
                    return _this.unsubscribe();
                };
                partialObserver = {
                    next: observerOrNext.next && $a21822406e53a1a5$var$bind(observerOrNext.next, context_1),
                    error: observerOrNext.error && $a21822406e53a1a5$var$bind(observerOrNext.error, context_1),
                    complete: observerOrNext.complete && $a21822406e53a1a5$var$bind(observerOrNext.complete, context_1)
                };
            } else partialObserver = observerOrNext;
        }
        _this.destination = new $a21822406e53a1a5$var$ConsumerObserver(partialObserver);
        return _this;
    }
    return SafeSubscriber;
}($a21822406e53a1a5$export$60959659b2c22881);
function $a21822406e53a1a5$var$handleUnhandledError(error) {
    if ((0, $130e6ab1a4f4ab41$export$e506a1d27d1eaa20).useDeprecatedSynchronousErrorHandling) (0, $db83674f177e2fa2$export$3e205fbb5df021e0)(error);
    else (0, $0b985c2ba288a978$export$ebf645db02d3e99f)(error);
}
function $a21822406e53a1a5$var$defaultErrorHandler(err) {
    throw err;
}
function $a21822406e53a1a5$var$handleStoppedNotification(notification, subscriber) {
    var onStoppedNotification = (0, $130e6ab1a4f4ab41$export$e506a1d27d1eaa20).onStoppedNotification;
    onStoppedNotification && (0, $54e99c791bb8207c$export$365aa6bd3c788e3d).setTimeout(function() {
        return onStoppedNotification(notification, subscriber);
    });
}
var $a21822406e53a1a5$export$88d395d20619a0af = {
    closed: true,
    next: (0, $09d0e01f28fdde08$export$8793edee2d425525),
    error: $a21822406e53a1a5$var$defaultErrorHandler,
    complete: (0, $09d0e01f28fdde08$export$8793edee2d425525)
};



var $fb5600b8b605d96c$export$5ab46cbf6120b33a = function() {
    return typeof Symbol === 'function' && Symbol.observable || '@@observable';
}();


function $d1675d7b83173212$export$f0954fd7d5368655(x) {
    return x;
}


function $f9bfc0ec752e7991$export$a4627e546088548d() {
    var fns = [];
    for(var _i = 0; _i < arguments.length; _i++)fns[_i] = arguments[_i];
    return $f9bfc0ec752e7991$export$71f88a61afaa14d7(fns);
}
function $f9bfc0ec752e7991$export$71f88a61afaa14d7(fns) {
    if (fns.length === 0) return 0, $d1675d7b83173212$export$f0954fd7d5368655;
    if (fns.length === 1) return fns[0];
    return function piped(input) {
        return fns.reduce(function(prev, fn) {
            return fn(prev);
        }, input);
    };
}





var $c3941cf54d4aa4ba$export$77cea355fa80b5f4 = function() {
    function Observable(subscribe) {
        if (subscribe) this._subscribe = subscribe;
    }
    Observable.prototype.lift = function(operator) {
        var observable = new Observable();
        observable.source = this;
        observable.operator = operator;
        return observable;
    };
    Observable.prototype.subscribe = function(observerOrNext, error, complete) {
        var _this = this;
        var subscriber = $c3941cf54d4aa4ba$var$isSubscriber(observerOrNext) ? observerOrNext : new (0, $a21822406e53a1a5$export$94eeb6a328cab6c7)(observerOrNext, error, complete);
        (0, $db83674f177e2fa2$export$d974811edc77eafa)(function() {
            var _a = _this, operator = _a.operator, source = _a.source;
            subscriber.add(operator ? operator.call(subscriber, source) : source ? _this._subscribe(subscriber) : _this._trySubscribe(subscriber));
        });
        return subscriber;
    };
    Observable.prototype._trySubscribe = function(sink) {
        try {
            return this._subscribe(sink);
        } catch (err) {
            sink.error(err);
        }
    };
    Observable.prototype.forEach = function(next, promiseCtor) {
        var _this = this;
        promiseCtor = $c3941cf54d4aa4ba$var$getPromiseCtor(promiseCtor);
        return new promiseCtor(function(resolve, reject) {
            var subscriber = new (0, $a21822406e53a1a5$export$94eeb6a328cab6c7)({
                next: function(value) {
                    try {
                        next(value);
                    } catch (err) {
                        reject(err);
                        subscriber.unsubscribe();
                    }
                },
                error: reject,
                complete: resolve
            });
            _this.subscribe(subscriber);
        });
    };
    Observable.prototype._subscribe = function(subscriber) {
        var _a;
        return (_a = this.source) === null || _a === void 0 ? void 0 : _a.subscribe(subscriber);
    };
    Observable.prototype[0, $fb5600b8b605d96c$export$5ab46cbf6120b33a] = function() {
        return this;
    };
    Observable.prototype.pipe = function() {
        var operations = [];
        for(var _i = 0; _i < arguments.length; _i++)operations[_i] = arguments[_i];
        return (0, $f9bfc0ec752e7991$export$71f88a61afaa14d7)(operations)(this);
    };
    Observable.prototype.toPromise = function(promiseCtor) {
        var _this = this;
        promiseCtor = $c3941cf54d4aa4ba$var$getPromiseCtor(promiseCtor);
        return new promiseCtor(function(resolve, reject) {
            var value;
            _this.subscribe(function(x) {
                return value = x;
            }, function(err) {
                return reject(err);
            }, function() {
                return resolve(value);
            });
        });
    };
    Observable.create = function(subscribe) {
        return new Observable(subscribe);
    };
    return Observable;
}();
function $c3941cf54d4aa4ba$var$getPromiseCtor(promiseCtor) {
    var _a;
    return (_a = promiseCtor !== null && promiseCtor !== void 0 ? promiseCtor : (0, $130e6ab1a4f4ab41$export$e506a1d27d1eaa20).Promise) !== null && _a !== void 0 ? _a : Promise;
}
function $c3941cf54d4aa4ba$var$isObserver(value) {
    return value && (0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(value.next) && (0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(value.error) && (0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(value.complete);
}
function $c3941cf54d4aa4ba$var$isSubscriber(value) {
    return value && value instanceof (0, $a21822406e53a1a5$export$60959659b2c22881) || $c3941cf54d4aa4ba$var$isObserver(value) && (0, $b1d1a261220cb194$export$4f9221cbada3c4fc)(value);
}




var $05f7a3630bce7cef$export$f708eee25634bc80 = (0, $df5c9cb6183aeff9$export$128a15b65d1b6041)(function(_super) {
    return function ObjectUnsubscribedErrorImpl() {
        _super(this);
        this.name = 'ObjectUnsubscribedError';
        this.message = 'object unsubscribed';
    };
});




var $c9189cc69fd31ace$export$b6bbab5a9b109038 = function(_super) {
    (0, $8e874a2fb001c377$export$a8ba968b8961cb8a)(Subject, _super);
    function Subject() {
        var _this = _super.call(this) || this;
        _this.closed = false;
        _this.currentObservers = null;
        _this.observers = [];
        _this.isStopped = false;
        _this.hasError = false;
        _this.thrownError = null;
        return _this;
    }
    Subject.prototype.lift = function(operator) {
        var subject = new $c9189cc69fd31ace$export$155189cde295587d(this, this);
        subject.operator = operator;
        return subject;
    };
    Subject.prototype._throwIfClosed = function() {
        if (this.closed) throw new (0, $05f7a3630bce7cef$export$f708eee25634bc80)();
    };
    Subject.prototype.next = function(value) {
        var _this = this;
        (0, $db83674f177e2fa2$export$d974811edc77eafa)(function() {
            var e_1, _a;
            _this._throwIfClosed();
            if (!_this.isStopped) {
                if (!_this.currentObservers) _this.currentObservers = Array.from(_this.observers);
                try {
                    for(var _b = (0, $8e874a2fb001c377$export$19a8beecd37a4c45)(_this.currentObservers), _c = _b.next(); !_c.done; _c = _b.next()){
                        var observer = _c.value;
                        observer.next(value);
                    }
                } catch (e_1_1) {
                    e_1 = {
                        error: e_1_1
                    };
                } finally{
                    try {
                        if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                    } finally{
                        if (e_1) throw e_1.error;
                    }
                }
            }
        });
    };
    Subject.prototype.error = function(err) {
        var _this = this;
        (0, $db83674f177e2fa2$export$d974811edc77eafa)(function() {
            _this._throwIfClosed();
            if (!_this.isStopped) {
                _this.hasError = _this.isStopped = true;
                _this.thrownError = err;
                var observers = _this.observers;
                while(observers.length)observers.shift().error(err);
            }
        });
    };
    Subject.prototype.complete = function() {
        var _this = this;
        (0, $db83674f177e2fa2$export$d974811edc77eafa)(function() {
            _this._throwIfClosed();
            if (!_this.isStopped) {
                _this.isStopped = true;
                var observers = _this.observers;
                while(observers.length)observers.shift().complete();
            }
        });
    };
    Subject.prototype.unsubscribe = function() {
        this.isStopped = this.closed = true;
        this.observers = this.currentObservers = null;
    };
    Object.defineProperty(Subject.prototype, "observed", {
        get: function() {
            var _a;
            return ((_a = this.observers) === null || _a === void 0 ? void 0 : _a.length) > 0;
        },
        enumerable: false,
        configurable: true
    });
    Subject.prototype._trySubscribe = function(subscriber) {
        this._throwIfClosed();
        return _super.prototype._trySubscribe.call(this, subscriber);
    };
    Subject.prototype._subscribe = function(subscriber) {
        this._throwIfClosed();
        this._checkFinalizedStatuses(subscriber);
        return this._innerSubscribe(subscriber);
    };
    Subject.prototype._innerSubscribe = function(subscriber) {
        var _this = this;
        var _a = this, hasError = _a.hasError, isStopped = _a.isStopped, observers = _a.observers;
        if (hasError || isStopped) return 0, $b1d1a261220cb194$export$610f9c3ca1a38dd8;
        this.currentObservers = null;
        observers.push(subscriber);
        return new (0, $b1d1a261220cb194$export$f55210826850c514)(function() {
            _this.currentObservers = null;
            (0, $a53e47be1b60c3c3$export$dae3f38077fc36c0)(observers, subscriber);
        });
    };
    Subject.prototype._checkFinalizedStatuses = function(subscriber) {
        var _a = this, hasError = _a.hasError, thrownError = _a.thrownError, isStopped = _a.isStopped;
        if (hasError) subscriber.error(thrownError);
        else if (isStopped) subscriber.complete();
    };
    Subject.prototype.asObservable = function() {
        var observable = new (0, $c3941cf54d4aa4ba$export$77cea355fa80b5f4)();
        observable.source = this;
        return observable;
    };
    Subject.create = function(destination, source) {
        return new $c9189cc69fd31ace$export$155189cde295587d(destination, source);
    };
    return Subject;
}((0, $c3941cf54d4aa4ba$export$77cea355fa80b5f4));
var $c9189cc69fd31ace$export$155189cde295587d = function(_super) {
    (0, $8e874a2fb001c377$export$a8ba968b8961cb8a)(AnonymousSubject, _super);
    function AnonymousSubject(destination, source) {
        var _this = _super.call(this) || this;
        _this.destination = destination;
        _this.source = source;
        return _this;
    }
    AnonymousSubject.prototype.next = function(value) {
        var _a, _b;
        (_b = (_a = this.destination) === null || _a === void 0 ? void 0 : _a.next) === null || _b === void 0 || _b.call(_a, value);
    };
    AnonymousSubject.prototype.error = function(err) {
        var _a, _b;
        (_b = (_a = this.destination) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 || _b.call(_a, err);
    };
    AnonymousSubject.prototype.complete = function() {
        var _a, _b;
        (_b = (_a = this.destination) === null || _a === void 0 ? void 0 : _a.complete) === null || _b === void 0 || _b.call(_a);
    };
    AnonymousSubject.prototype._subscribe = function(subscriber) {
        var _a, _b;
        return (_b = (_a = this.source) === null || _a === void 0 ? void 0 : _a.subscribe(subscriber)) !== null && _b !== void 0 ? _b : (0, $b1d1a261220cb194$export$610f9c3ca1a38dd8);
    };
    return AnonymousSubject;
}($c9189cc69fd31ace$export$b6bbab5a9b109038);





var $3ee960211923e35c$export$e19cd5f9376f8cee = function(_super) {
    (0, $8e874a2fb001c377$export$a8ba968b8961cb8a)(Action, _super);
    function Action(scheduler, work) {
        return _super.call(this) || this;
    }
    Action.prototype.schedule = function(state, delay) {
        if (delay === void 0) delay = 0;
        return this;
    };
    return Action;
}((0, $b1d1a261220cb194$export$f55210826850c514));



var $ccf264af78c278c5$export$f0ca5e16f6f68711 = {
    setInterval: function(handler, timeout) {
        var args = [];
        for(var _i = 2; _i < arguments.length; _i++)args[_i - 2] = arguments[_i];
        var delegate = $ccf264af78c278c5$export$f0ca5e16f6f68711.delegate;
        if (delegate === null || delegate === void 0 ? void 0 : delegate.setInterval) return delegate.setInterval.apply(delegate, (0, $8e874a2fb001c377$export$1216008129fb82ed)([
            handler,
            timeout
        ], (0, $8e874a2fb001c377$export$8d051b38c9118094)(args)));
        return setInterval.apply(void 0, (0, $8e874a2fb001c377$export$1216008129fb82ed)([
            handler,
            timeout
        ], (0, $8e874a2fb001c377$export$8d051b38c9118094)(args)));
    },
    clearInterval: function(handle) {
        var delegate = $ccf264af78c278c5$export$f0ca5e16f6f68711.delegate;
        return ((delegate === null || delegate === void 0 ? void 0 : delegate.clearInterval) || clearInterval)(handle);
    },
    delegate: undefined
};



var $9becc74b1b373225$export$b5369ffd7cb2211b = function(_super) {
    (0, $8e874a2fb001c377$export$a8ba968b8961cb8a)(AsyncAction, _super);
    function AsyncAction(scheduler, work) {
        var _this = _super.call(this, scheduler, work) || this;
        _this.scheduler = scheduler;
        _this.work = work;
        _this.pending = false;
        return _this;
    }
    AsyncAction.prototype.schedule = function(state, delay) {
        var _a;
        if (delay === void 0) delay = 0;
        if (this.closed) return this;
        this.state = state;
        var id = this.id;
        var scheduler = this.scheduler;
        if (id != null) this.id = this.recycleAsyncId(scheduler, id, delay);
        this.pending = true;
        this.delay = delay;
        this.id = (_a = this.id) !== null && _a !== void 0 ? _a : this.requestAsyncId(scheduler, this.id, delay);
        return this;
    };
    AsyncAction.prototype.requestAsyncId = function(scheduler, _id, delay) {
        if (delay === void 0) delay = 0;
        return (0, $ccf264af78c278c5$export$f0ca5e16f6f68711).setInterval(scheduler.flush.bind(scheduler, this), delay);
    };
    AsyncAction.prototype.recycleAsyncId = function(_scheduler, id, delay) {
        if (delay === void 0) delay = 0;
        if (delay != null && this.delay === delay && this.pending === false) return id;
        if (id != null) (0, $ccf264af78c278c5$export$f0ca5e16f6f68711).clearInterval(id);
        return undefined;
    };
    AsyncAction.prototype.execute = function(state, delay) {
        if (this.closed) return new Error('executing a cancelled action');
        this.pending = false;
        var error = this._execute(state, delay);
        if (error) return error;
        else if (this.pending === false && this.id != null) this.id = this.recycleAsyncId(this.scheduler, this.id, null);
    };
    AsyncAction.prototype._execute = function(state, _delay) {
        var errored = false;
        var errorValue;
        try {
            this.work(state);
        } catch (e) {
            errored = true;
            errorValue = e ? e : new Error('Scheduled action threw falsy error');
        }
        if (errored) {
            this.unsubscribe();
            return errorValue;
        }
    };
    AsyncAction.prototype.unsubscribe = function() {
        if (!this.closed) {
            var _a = this, id = _a.id, scheduler = _a.scheduler;
            var actions = scheduler.actions;
            this.work = this.state = this.scheduler = null;
            this.pending = false;
            (0, $a53e47be1b60c3c3$export$dae3f38077fc36c0)(actions, this);
            if (id != null) this.id = this.recycleAsyncId(scheduler, id, null);
            this.delay = null;
            _super.prototype.unsubscribe.call(this);
        }
    };
    return AsyncAction;
}((0, $3ee960211923e35c$export$e19cd5f9376f8cee));



var $bd698e8115fc86c4$export$c1895ea2d519a677 = {
    now: function() {
        return ($bd698e8115fc86c4$export$c1895ea2d519a677.delegate || Date).now();
    },
    delegate: undefined
};


var $afddd99de6dafc81$export$d3e54bb8584cda7c = function() {
    function Scheduler(schedulerActionCtor, now) {
        if (now === void 0) now = Scheduler.now;
        this.schedulerActionCtor = schedulerActionCtor;
        this.now = now;
    }
    Scheduler.prototype.schedule = function(work, delay, state) {
        if (delay === void 0) delay = 0;
        return new this.schedulerActionCtor(this, work).schedule(state, delay);
    };
    Scheduler.now = (0, $bd698e8115fc86c4$export$c1895ea2d519a677).now;
    return Scheduler;
}();


var $49202f47f01b459a$export$4114a3ec92faa3c6 = function(_super) {
    (0, $8e874a2fb001c377$export$a8ba968b8961cb8a)(AsyncScheduler, _super);
    function AsyncScheduler(SchedulerAction, now) {
        if (now === void 0) now = (0, $afddd99de6dafc81$export$d3e54bb8584cda7c).now;
        var _this = _super.call(this, SchedulerAction, now) || this;
        _this.actions = [];
        _this._active = false;
        return _this;
    }
    AsyncScheduler.prototype.flush = function(action) {
        var actions = this.actions;
        if (this._active) {
            actions.push(action);
            return;
        }
        var error;
        this._active = true;
        do {
            if (error = action.execute(action.state, action.delay)) break;
        }while (action = actions.shift());
        this._active = false;
        if (error) {
            while(action = actions.shift())action.unsubscribe();
            throw error;
        }
    };
    return AsyncScheduler;
}((0, $afddd99de6dafc81$export$d3e54bb8584cda7c));


var $f6c4c9bd71b20294$export$8d6b71a2b178fc32 = new (0, $49202f47f01b459a$export$4114a3ec92faa3c6)((0, $9becc74b1b373225$export$b5369ffd7cb2211b));
var $f6c4c9bd71b20294$export$12af436354f1a4f7 = $f6c4c9bd71b20294$export$8d6b71a2b178fc32;



function $dd125016ed30f8c3$export$187e35932e88c15c(value) {
    return value && (0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(value.schedule);
}


function $c2526ac6c756a544$export$c03bb0116ee09d4b(value) {
    return value instanceof Date && !isNaN(value);
}


function $dd4b589699967386$export$9dc4ecf953986f04(dueTime, intervalOrScheduler, scheduler) {
    if (dueTime === void 0) dueTime = 0;
    if (scheduler === void 0) scheduler = (0, $f6c4c9bd71b20294$export$12af436354f1a4f7);
    var intervalDuration = -1;
    if (intervalOrScheduler != null) {
        if ((0, $dd125016ed30f8c3$export$187e35932e88c15c)(intervalOrScheduler)) scheduler = intervalOrScheduler;
        else intervalDuration = intervalOrScheduler;
    }
    return new (0, $c3941cf54d4aa4ba$export$77cea355fa80b5f4)(function(subscriber) {
        var due = (0, $c2526ac6c756a544$export$c03bb0116ee09d4b)(dueTime) ? +dueTime - scheduler.now() : dueTime;
        if (due < 0) due = 0;
        var n = 0;
        return scheduler.schedule(function() {
            if (!subscriber.closed) {
                subscriber.next(n++);
                if (0 <= intervalDuration) this.schedule(undefined, intervalDuration);
                else subscriber.complete();
            }
        }, due);
    });
}



function $dd48a069f37f5009$export$ebec8610810318c5(source) {
    return (0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(source === null || source === void 0 ? void 0 : source.lift);
}
function $dd48a069f37f5009$export$871731df685d7753(init) {
    return function(source) {
        if ($dd48a069f37f5009$export$ebec8610810318c5(source)) return source.lift(function(liftedSource) {
            try {
                return init(liftedSource, this);
            } catch (err) {
                this.error(err);
            }
        });
        throw new TypeError('Unable to lift unknown Observable type');
    };
}




function $f91d17b1cd8d7dd0$export$b9e075132cc6937f(destination, onNext, onComplete, onError, onFinalize) {
    return new $f91d17b1cd8d7dd0$export$8fbbf0c4d80268e5(destination, onNext, onComplete, onError, onFinalize);
}
var $f91d17b1cd8d7dd0$export$8fbbf0c4d80268e5 = function(_super) {
    (0, $8e874a2fb001c377$export$a8ba968b8961cb8a)(OperatorSubscriber, _super);
    function OperatorSubscriber(destination, onNext, onComplete, onError, onFinalize, shouldUnsubscribe) {
        var _this = _super.call(this, destination) || this;
        _this.onFinalize = onFinalize;
        _this.shouldUnsubscribe = shouldUnsubscribe;
        _this._next = onNext ? function(value) {
            try {
                onNext(value);
            } catch (err) {
                destination.error(err);
            }
        } : _super.prototype._next;
        _this._error = onError ? function(err) {
            try {
                onError(err);
            } catch (err) {
                destination.error(err);
            } finally{
                this.unsubscribe();
            }
        } : _super.prototype._error;
        _this._complete = onComplete ? function() {
            try {
                onComplete();
            } catch (err) {
                destination.error(err);
            } finally{
                this.unsubscribe();
            }
        } : _super.prototype._complete;
        return _this;
    }
    OperatorSubscriber.prototype.unsubscribe = function() {
        var _a;
        if (!this.shouldUnsubscribe || this.shouldUnsubscribe()) {
            var closed_1 = this.closed;
            _super.prototype.unsubscribe.call(this);
            !closed_1 && ((_a = this.onFinalize) === null || _a === void 0 || _a.call(this));
        }
    };
    return OperatorSubscriber;
}((0, $a21822406e53a1a5$export$60959659b2c22881));



function $b0ba2d9822618ffe$export$3f23594af5f37336(observerOrNext, error, complete) {
    var tapObserver = (0, $9f4cd17d3bfb739b$export$f6e2535fb5126e54)(observerOrNext) || error || complete ? {
        next: observerOrNext,
        error: error,
        complete: complete
    } : observerOrNext;
    return tapObserver ? (0, $dd48a069f37f5009$export$871731df685d7753)(function(source, subscriber) {
        var _a;
        (_a = tapObserver.subscribe) === null || _a === void 0 || _a.call(tapObserver);
        var isUnsub = true;
        source.subscribe((0, $f91d17b1cd8d7dd0$export$b9e075132cc6937f)(subscriber, function(value) {
            var _a;
            (_a = tapObserver.next) === null || _a === void 0 || _a.call(tapObserver, value);
            subscriber.next(value);
        }, function() {
            var _a;
            isUnsub = false;
            (_a = tapObserver.complete) === null || _a === void 0 || _a.call(tapObserver);
            subscriber.complete();
        }, function(err) {
            var _a;
            isUnsub = false;
            (_a = tapObserver.error) === null || _a === void 0 || _a.call(tapObserver, err);
            subscriber.error(err);
        }, function() {
            var _a, _b;
            if (isUnsub) (_a = tapObserver.unsubscribe) === null || _a === void 0 || _a.call(tapObserver);
            (_b = tapObserver.finalize) === null || _b === void 0 || _b.call(tapObserver);
        }));
    }) : (0, $d1675d7b83173212$export$f0954fd7d5368655);
}


/**
 * Owns event buffering and look-ahead scheduling.
 *
 * Pulls batches of events from the {@link EventBatchSource} into a buffer,
 * and every `lookAhead` milliseconds converts enough of them into timestamped
 * events to fill the `scheduleAheadTime` window, publishing them on
 * {@link events$}. Timestamps are derived from the injected clock, so the
 * caller decides which clock the schedule runs against.
 *
 * Durations arrive in beats and are converted to seconds here, so tempo
 * changes apply live, within the look-ahead window. Structural changes take
 * effect when the next batch is pulled from the source.
 *
 * This is deliberately pull-based (the classic Web Audio "two clocks"
 * pattern): audio scheduling must run ahead of wall-clock time, which a
 * push-based stream cannot do on its own.
 */ class $c766c855537a2d6d$export$d3e54bb8584cda7c {
    config;
    currentTime;
    // how often to call schedule(), milliseconds
    lookAhead = 25;
    // how far ahead to schedule audio, seconds
    scheduleAheadTime = 0.1;
    schedulerSubscription;
    buffer = [];
    // the most recently scheduled event, used to chain the next timestamp;
    // also holds a zero-duration anchor before anything is scheduled
    lastScheduled;
    eventSubject = new (0, $c9189cc69fd31ace$export$b6bbab5a9b109038)();
    events$ = this.eventSubject;
    scheduler$ = (0, $dd4b589699967386$export$9dc4ecf953986f04)(0, this.lookAhead).pipe((0, $b0ba2d9822618ffe$export$3f23594af5f37336)(()=>this.schedule()));
    /**
     * Duration of one beat in seconds. Read live so tempo changes apply to
     * the next scheduled event, within the look-ahead window.
     */ get beatDuration() {
        return 60 / this.config.bpm();
    }
    constructor(config, currentTime){
        this.config = config;
        this.currentTime = currentTime;
    }
    /**
     * Emit the seed events (each with its duration in beats, e.g. a count-in)
     * and start the look-ahead loop, which chains off the last seed's
     * timestamp. With an empty seed, generation starts one
     * `scheduleAheadTime` from now.
     */ start(seed = []) {
        let timestamp = this.currentTime() + this.scheduleAheadTime;
        // anchor for the schedule loop to chain from; overwritten by each seed
        this.lastScheduled = {
            duration: 0,
            timestamp: timestamp
        };
        for (const event of seed){
            const duration = event.duration * this.beatDuration;
            this.lastScheduled = {
                duration: duration,
                timestamp: timestamp
            };
            this.eventSubject.next({
                duration: duration,
                timestamp: timestamp,
                event: event
            });
            timestamp += duration;
        }
        this.schedulerSubscription = this.scheduler$.subscribe();
    }
    /**
     * Stop scheduling and clear the buffered events, so the next start begins
     * cleanly on a batch boundary.
     */ stop() {
        this.schedulerSubscription?.unsubscribe();
        this.buffer = [];
        this.lastScheduled = undefined;
    }
    schedule() {
        const previous = this.lastScheduled;
        if (!previous) return;
        let nextTimestamp = previous.timestamp + previous.duration;
        while(nextTimestamp < this.currentTime() + this.scheduleAheadTime){
            const event = this.nextEvent();
            const duration = event.duration * this.beatDuration;
            const scheduled = {
                duration: duration,
                timestamp: nextTimestamp,
                event: event
            };
            this.lastScheduled = {
                duration: duration,
                timestamp: nextTimestamp
            };
            this.eventSubject.next(scheduled);
            nextTimestamp += duration;
        }
    }
    nextEvent() {
        if (this.buffer.length === 0) // synchronously refills the buffer with a fresh batch
        this.buffer = this.config.source();
        return this.buffer.shift();
    }
}


/**
 * Biased random integer in [min, max], centred on a target derived from
 * `biasPercent` (1 -> min, 100 -> max).
 *
 * Draws a single uniform value from a window centred on the target and
 * rounds it. The window spans the whole range mid-range and narrows towards
 * the edges, which keeps the mean on the target: for a two-value range
 * (e.g. 0..1) `max` is returned with probability `biasPercent / 100`.
 *
 * @param min
 * @param max
 * @param biasPercent - range 1..100. passing 1 (or less) always returns min, 100 (or more) always returns max
 */ function $910fcc7bce5be3ba$export$b4da2a2d574a34b7(min, max, biasPercent = 50) {
    if (biasPercent <= 1) return min;
    if (biasPercent >= 100) return max;
    const range = max - min;
    const target = min + range * (biasPercent / 100);
    // symmetric noise around the target; full spread mid-range, narrowing
    // towards the edges so the mean stays on the target
    const halfWidth = Math.min(range / 2, target - min + 0.5, max - target + 0.5);
    const noise = (Math.random() * 2 - 1) * halfWidth;
    return Math.min(Math.max(Math.round(target + noise), min), max);
}
/**
 * Given a range, a 'bin' count and a value, return
 * the index of the value into a bin. Useful for converting UI
 * slider ranges into discrete indexes.
 * @param min
 * @param max
 * @param count
 * @param value
 */ function $910fcc7bce5be3ba$export$215181f5b8457fa2(min, max, count, value) {
    if (value < min || value > max) throw new Error('value must be within range');
    // 1. Edge case: max maps to the last valid index (count - 1)
    if (value === max) return count - 1;
    const normalizedValue = value - min;
    const rangeSpan = max - min;
    return Math.floor(normalizedValue / rangeSpan * count);
}





/**
 * A demand-driven source of bars for the {@link Scheduler}: each call
 * generates one bar of {@link BarEvent}s. Nothing is generated until the
 * scheduler's buffer runs dry and it pulls the next batch.
 *
 * Structural settings (beatsPerBar) are read live from `settings`, so changes
 * take effect when the next bar is generated.
 */ function $01e01fc75c54d004$export$dd2baebf4b98e032(generator, settings) {
    return ()=>(0, $efdf968da99467e3$export$bffa455ba8c619a6)(generator.generate(), settings.beatsPerBar).map((rhythmEvent, index)=>({
                duration: rhythmEvent.duration,
                isRest: index === 0 && settings.hasDownbeatAlways ? false : (0, $910fcc7bce5be3ba$export$b4da2a2d574a34b7)(0, 1, settings.spaceBias) === 1,
                isDownbeat: index === 0
            }));
}


/**
 * Clicker-side adapter over the kernel {@link Scheduler}: wires the bar tree
 * generator and bar event source from {@link Settings} (read live, so tempo
 * changes apply within the look-ahead window and structural changes take
 * effect when the next bar is generated), and pre-schedules the count-in.
 */ class $c846e1a463e836ea$export$e35a6eeb699072ae {
    settings;
    scheduler;
    onsets$;
    constructor(settings, currentTime){
        this.settings = settings;
        const generator = new (0, $2b01753db8423e69$export$b135b2512b192788)(settings);
        this.scheduler = new (0, $c766c855537a2d6d$export$d3e54bb8584cda7c)({
            bpm: ()=>settings.bpm,
            source: (0, $01e01fc75c54d004$export$dd2baebf4b98e032)(generator, settings)
        }, currentTime);
        this.onsets$ = this.scheduler.events$;
    }
    /**
     * Pre-schedule the count-in (beatsPerBar evenly spaced, always sounding,
     * with the first as the downbeat) and start the look-ahead loop.
     */ start() {
        const countIn = [];
        for(let i = 0; i < this.settings.beatsPerBar; i++)countIn.push({
            duration: 1,
            isRest: false,
            isDownbeat: i === 0
        });
        this.scheduler.start(countIn);
    }
    /**
     * Stop scheduling; the next start begins cleanly at a downbeat.
     */ stop() {
        this.scheduler.stop();
    }
}


/**
 * Clicker's composition module: owns its cards' control bindings and the
 * lazy audio construction (AudioContext created on first user gesture).
 * Extracted from the original Main class; behaviour unchanged.
 */ class $b01c745e30ba2459$export$5d0071c67bc537b2 {
    settings;
    audio;
    scheduler;
    playing = false;
    constructor(){
        this.settings = $b01c745e30ba2459$var$loadClickerSettings();
        this.initialise();
    }
    get isPlaying() {
        return this.playing;
    }
    /**
     * The player is created lazily, on the first user interaction that needs
     * audio (pressing play, or picking a sound in the Settings sheet), so
     * that the AudioContext is always created inside a user gesture.
     * @private
     */ getOrCreateAudio() {
        if (!this.audio) {
            const audio = new (0, $c523d1e73d11fc5d$export$ce4d7b336b893c95)(this.settings.downbeatSound, this.settings.secondarySound);
            this.audio = audio;
            this.scheduler = new (0, $c846e1a463e836ea$export$e35a6eeb699072ae)(this.settings, ()=>audio.currentTime);
            // rests and downbeats are already decided by the bar event stream;
            // the audio layer only maps them to a click type - no randomness here.
            this.scheduler.onsets$.subscribe((onset)=>{
                const clickType = onset.event.isRest ? 'silent' : onset.event.isDownbeat ? 'primary' : 'secondary';
                audio.click(onset.timestamp, clickType);
            });
        }
        return this.audio;
    }
    start() {
        this.playing = true;
        this.getOrCreateAudio().resume().then(()=>this.scheduler?.start());
    }
    onSoundSelected(section, sound) {
        const key = section === 'downbeat' ? 'downbeatSound' : 'secondarySound';
        this.settings[key] = sound;
        localStorage.setItem(key, sound);
        const audio = this.getOrCreateAudio();
        if (section === 'downbeat') audio.setPrimarySound(sound);
        else audio.setSecondarySound(sound);
        if (!this.isPlaying) audio.preview(sound);
    }
    stop() {
        this.playing = false;
        this.scheduler?.stop();
        this.audio?.stop();
        this.syncPlayControl();
    }
    /**
     * Wire up the UI listeners
     * @private
     */ initialise() {
        const playCheckbox = document.getElementById('play-checkbox');
        if (playCheckbox) playCheckbox.addEventListener('click', (event)=>{
            const isPlaying = event.target.checked;
            if (isPlaying) this.start();
            else this.stop();
            this.syncPlayControl();
        });
        const bind = (id, key)=>(0, $d37efb89f06c4220$export$4fc2d638ed78045a)(id, ()=>this.settings[key], (value)=>{
                localStorage.setItem(key, '' + value);
                this.settings[key] = value;
            });
        bind('bpm', 'bpm');
        (0, $d37efb89f06c4220$export$e0a99e6a99ef25a0)('bpm-output', 'bpm-input', 'bpm');
        bind('beatsPerBar', 'beatsPerBar');
        bind('granularity', 'granularity');
        bind('space', 'spaceBias');
        bind('tuplets', 'tupletBias');
        (0, $d37efb89f06c4220$export$913f1d0f2a072d8c)('hasDownbeatAlways', ()=>this.settings.hasDownbeatAlways, (isChecked)=>{
            localStorage.setItem('hasDownbeatAlways', JSON.stringify(isChecked));
            this.settings.hasDownbeatAlways = isChecked;
        });
        (0, $d37efb89f06c4220$export$c2f691affe889eb1)(document.getElementById('tool-clicker'));
    }
    /** Keep the play toggle's visual/aria state in sync with the playing state. */ syncPlayControl() {
        const playCheckbox = document.getElementById('play-checkbox');
        const playControl = playCheckbox?.closest('.play-control');
        if (!playCheckbox || !playControl) return;
        playCheckbox.checked = this.playing;
        playControl.classList.toggle('playing', this.playing);
        playControl.setAttribute('aria-label', this.playing ? 'Stop' : 'Play');
    }
}
/** Load Clicker's settings from localStorage (bare keys, unchanged). */ function $b01c745e30ba2459$var$loadClickerSettings() {
    return {
        bpm: +(localStorage.getItem('bpm') || 80),
        beatsPerBar: +(localStorage.getItem('beatsPerBar') || 4),
        granularity: +(localStorage.getItem('granularity') || 1),
        tupletBias: +(localStorage.getItem('tupletBias') || 1),
        spaceBias: +(localStorage.getItem('spaceBias') || 1),
        hasDownbeatAlways: localStorage.getItem('hasDownbeatAlways') !== 'false',
        downbeatSound: (0, $b7bbc4f8d421aeee$export$8bcf5b6f3ecd9d6d)(localStorage.getItem('downbeatSound'), (0, $b7bbc4f8d421aeee$export$eb5f9c6e38f3e159)),
        secondarySound: (0, $b7bbc4f8d421aeee$export$8bcf5b6f3ecd9d6d)(localStorage.getItem('secondarySound'), (0, $b7bbc4f8d421aeee$export$67fdbea1e102c657))
    };
}





/**
 * Map the density slider (1..100) to a maxNoteCount for the phrase flow.
 * Scaling through beatsPerBar keeps density comparable across meters
 * (50 in 4/4 is about 8 notes, in 3/4 about 6); the floor keeps every
 * phrase sounding at least the downbeat note. Four notes per beat at
 * density 100 is the practical ceiling.
 */ function $c38d17e4adf57e4d$export$86a570d26acd92b7(density, beatsPerBar) {
    return Math.max(1, Math.round(density / 100 * beatsPerBar * 4));
}


/**
 * NoteSelection - owns which pitches can sound in the Phraser tool.
 *
 * State is a pitch-class selection (a subset of the 12 pitch classes
 * 0..11), a range window stored as offsets (0..36) within a range track,
 * and an octave offset (-2..+2) that shifts the track as a whole. The
 * track spans C3-C6 (MIDI 48..84), so with the octave offset the
 * reachable register is MIDI 24..108. The sounded set is always derived,
 * never stored: every note in the range window whose pitch class is
 * selected.
 *
 * Rotation is a bijection: rotate(semitones) maps every selected class
 * through mod12(pc + semitones), a reversible permutation of the
 * 12-class domain with no wrap fixup of any kind.
 *
 * The range window is stored as offsets from the track bottom, so the
 * octave carry-along is structural: setOctave changes one field and the
 * window can never leave the track. The setters clamp each thumb against
 * the other, mirroring the dual-thumb slider; low == high is allowed
 * (single-note drilling).
 *
 * Persistence follows the Clicker settings pattern: one localStorage key
 * per field, read at construction, written on change. When localStorage
 * is unavailable (tests), persistence is skipped silently. The dead keys
 * phraser.baseSelection and phraser.transpose are never read or written;
 * stale values are left alone - no migration, no cleanup.
 *
 * See docs/specs/range-window-model.md for the full semantics and
 * rationale.
 */ /** Track span: C3-C6 at octave 0, 37 positions (three octaves + one semitone). */ const $8c1a597dba073d78$var$TRACK_BOTTOM = 48;
const $8c1a597dba073d78$var$TRACK_TOP = 84;
const $8c1a597dba073d78$var$TRACK_LENGTH = $8c1a597dba073d78$var$TRACK_TOP - $8c1a597dba073d78$var$TRACK_BOTTOM; // 36
/** Default: C major classes, window covering C4-C5 of the C3-C6 track. */ const $8c1a597dba073d78$var$DEFAULT_PITCH_CLASSES = [
    0,
    2,
    4,
    5,
    7,
    9,
    11
];
const $8c1a597dba073d78$var$DEFAULT_RANGE = [
    12,
    24
];
const $8c1a597dba073d78$var$MIN_OCTAVE = -2;
const $8c1a597dba073d78$var$MAX_OCTAVE = 2;
const $8c1a597dba073d78$var$STORAGE_KEYS = {
    pitchClasses: 'phraser.pitchClasses',
    range: 'phraser.range',
    octave: 'phraser.octave'
};
/**
 * Pitch-class names with the real music symbol codepoints (U+266F sharp,
 * U+266D flat), not ASCII # and b - the old phraser did the same via HTML
 * entities. The symbols render via per-glyph font fallback; the note button
 * styles append symbol-capable fonts to the stack for predictable coverage.
 */ const $8c1a597dba073d78$var$PITCH_CLASS_NAMES = [
    'C',
    'C\u266F/D\u266D',
    'D',
    'D\u266F/E\u266D',
    'E',
    'F',
    'F\u266F/G\u266D',
    'G',
    'G\u266F/A\u266D',
    'A',
    'A\u266F/B\u266D',
    'B'
];
const $8c1a597dba073d78$var$clamp = (value, min, max)=>Math.min(Math.max(value, min), max);
const $8c1a597dba073d78$var$mod12 = (n)=>(n % 12 + 12) % 12;
class $8c1a597dba073d78$export$c00259035dd4d77e {
    pitchClasses;
    rangeLow;
    rangeHigh;
    octave;
    constructor(){
        this.pitchClasses = this.loadPitchClasses();
        [this.rangeLow, this.rangeHigh] = this.loadRange();
        this.octave = $8c1a597dba073d78$var$clamp(this.loadNumber($8c1a597dba073d78$var$STORAGE_KEYS.octave, 0), $8c1a597dba073d78$var$MIN_OCTAVE, $8c1a597dba073d78$var$MAX_OCTAVE);
    }
    // ---------------------------------------------------------------------
    // Derivation (pure functions of state)
    // ---------------------------------------------------------------------
    /** The octave-shifted range track: [bottom, top] in absolute MIDI notes. */ trackWindow() {
        const bottom = $8c1a597dba073d78$var$TRACK_BOTTOM + 12 * this.octave;
        return [
            bottom,
            bottom + $8c1a597dba073d78$var$TRACK_LENGTH
        ];
    }
    /** The range window in absolute MIDI notes (always inside the track). */ rangeWindow() {
        const [trackBottom] = this.trackWindow();
        return [
            trackBottom + this.rangeLow,
            trackBottom + this.rangeHigh
        ];
    }
    /**
     * The notes that can actually sound: sorted, every note in the range
     * window whose pitch class is selected. Derived by iterating the
     * window (at most 37 positions) - obviously correct by construction.
     */ soundedNotes() {
        const [low, high] = this.rangeWindow();
        const notes = [];
        for(let n = low; n <= high; n++)if (this.pitchClasses.has($8c1a597dba073d78$var$mod12(n))) notes.push(n);
        return notes;
    }
    // ---------------------------------------------------------------------
    // Display support
    // ---------------------------------------------------------------------
    /** Checked state for the pitch-class button of class `pc` (0..11). */ isSelected(pc) {
        return this.pitchClasses.has(pc);
    }
    /** The 12 fixed pitch-class names; labels never move, only checkmarks do. */ pitchClassLabels() {
        return [
            ...$8c1a597dba073d78$var$PITCH_CLASS_NAMES
        ];
    }
    /** The range window as track offsets: thumb positions for the slider. */ rangeOffsets() {
        return [
            this.rangeLow,
            this.rangeHigh
        ];
    }
    /** Current octave offset, -2..+2. */ getOctave() {
        return this.octave;
    }
    // ---------------------------------------------------------------------
    // Mutation (the only way state changes)
    // ---------------------------------------------------------------------
    /**
     * Toggle pitch class `pc` (0..11) in the selection. Selection and
     * range are independent: toggling a class outside the current window
     * is allowed; it simply sounds nothing until the window covers it.
     */ togglePitchClass(pc) {
        if (this.pitchClasses.has(pc)) this.pitchClasses.delete(pc);
        else this.pitchClasses.add(pc);
        this.persistPitchClasses();
    }
    /**
     * Rotate the selection: map every selected class through
     * mod12(pc + semitones). A bijection on the 12-class domain - fully
     * reversible, rotate(12) is the identity, rotate(0) a true no-op,
     * no fixup of any kind. `semitones` is unrestricted.
     */ rotate(semitones) {
        const rotated = new Set();
        for (const pc of this.pitchClasses)rotated.add($8c1a597dba073d78$var$mod12(pc + semitones));
        this.pitchClasses = rotated;
        this.persistPitchClasses();
    }
    /** Set the low thumb (track offset), clamped to [0, rangeHigh]. */ setRangeLow(offset) {
        this.rangeLow = $8c1a597dba073d78$var$clamp(offset, 0, this.rangeHigh);
        this.persistRange();
    }
    /** Set the high thumb (track offset), clamped to [rangeLow, 36]. */ setRangeHigh(offset) {
        this.rangeHigh = $8c1a597dba073d78$var$clamp(offset, this.rangeLow, $8c1a597dba073d78$var$TRACK_LENGTH);
        this.persistRange();
    }
    /** Shift the whole track, clamped to -2..+2. The window carries along. */ setOctave(octaves) {
        this.octave = $8c1a597dba073d78$var$clamp(octaves, $8c1a597dba073d78$var$MIN_OCTAVE, $8c1a597dba073d78$var$MAX_OCTAVE);
        this.persist($8c1a597dba073d78$var$STORAGE_KEYS.octave, '' + this.octave);
    }
    // ---------------------------------------------------------------------
    // Persistence (one localStorage key per field; skipped when unavailable)
    // ---------------------------------------------------------------------
    storage() {
        return typeof localStorage === 'undefined' ? null : localStorage;
    }
    persist(key, value) {
        this.storage()?.setItem(key, value);
    }
    persistPitchClasses() {
        this.persist($8c1a597dba073d78$var$STORAGE_KEYS.pitchClasses, JSON.stringify([
            ...this.pitchClasses
        ]));
    }
    persistRange() {
        this.persist($8c1a597dba073d78$var$STORAGE_KEYS.range, JSON.stringify(this.rangeOffsets()));
    }
    loadNumber(key, fallback) {
        const stored = this.storage()?.getItem(key);
        return stored == null ? fallback : +stored;
    }
    loadPitchClasses() {
        const stored = this.storage()?.getItem($8c1a597dba073d78$var$STORAGE_KEYS.pitchClasses);
        if (stored == null) return new Set($8c1a597dba073d78$var$DEFAULT_PITCH_CLASSES);
        const classes = JSON.parse(stored);
        return new Set(classes.filter((pc)=>Number.isInteger(pc) && pc >= 0 && pc <= 11));
    }
    loadRange() {
        const stored = this.storage()?.getItem($8c1a597dba073d78$var$STORAGE_KEYS.range);
        if (stored == null) return [
            ...$8c1a597dba073d78$var$DEFAULT_RANGE
        ];
        const pair = JSON.parse(stored);
        const low = $8c1a597dba073d78$var$clamp(Number.isInteger(pair[0]) ? pair[0] : $8c1a597dba073d78$var$DEFAULT_RANGE[0], 0, $8c1a597dba073d78$var$TRACK_LENGTH);
        const high = $8c1a597dba073d78$var$clamp(Number.isInteger(pair[1]) ? pair[1] : $8c1a597dba073d78$var$DEFAULT_RANGE[1], low, $8c1a597dba073d78$var$TRACK_LENGTH);
        return [
            low,
            high
        ];
    }
}


/**
 * NoteSource - draws the pitches for a phrase.
 *
 * Stateful draw: uniform over the sounded set minus the previously drawn
 * note, so a note never repeats immediately (with a two-note sounded set
 * this is strict alternation). The exclusion is a filter over the live
 * sounded set on every next() call; the remembered note is never removed
 * from the selection. Filtering and resampling (loop until different)
 * produce the same distribution; filtering wins because it costs one
 * random call per next() and never loops.
 *
 * Two edge cases are deliberate:
 * - Single-note drilling: when only one note sounds, it repeats (the
 *   fallback to the full pool keeps next() total).
 * - Mid-play eviction: the live-read pattern means the remembered last
 *   note can leave the sounded set; filtering it out is then a no-op,
 *   which is exactly right.
 *
 * next() is a total function: it throws on an empty sounded set, which is
 * a programming error - the UI disables play in that state, so next() is
 * never called then.
 *
 * NoteSource knows nothing about rhythm, phrases, gaps, or scheduling.
 */ class $7b29b154c8c1639f$export$e4d8dd7e6c5dcdcc {
    selection;
    last = null;
    constructor(selection){
        this.selection = selection;
    }
    /** Uniform random draw from the sounded set minus the previous note. */ next() {
        const notes = this.selection.soundedNotes();
        if (notes.length === 0) throw new Error('NoteSource.next() called with an empty sounded set');
        const pool = notes.length > 1 ? notes.filter((n)=>n !== this.last) : notes;
        const drawn = pool[Math.floor(Math.random() * pool.length)];
        this.last = drawn;
        return drawn;
    }
}


const $2adaf782742743f1$var$OSC_TYPE = 'square';
/** Decay span: the note length, clamped. */ const $2adaf782742743f1$var$RELEASE_MIN_SECONDS = 0.7;
const $2adaf782742743f1$var$RELEASE_MAX_SECONDS = 2.0;
/** Fixed tail added to the decay span, so very short notes still decay. */ const $2adaf782742743f1$var$RELEASE_TAIL_OFFSET_SECONDS = 0.5;
/** Oscillator stops this far past the note end, so the decay tail completes. */ const $2adaf782742743f1$var$OSC_STOP_GRACE_PERIOD = 1.0;
/** Lowpass softens the oscillator's high harmonics. */ const $2adaf782742743f1$var$LOWPASS_FREQ = 2500;
const $2adaf782742743f1$var$LOWPASS_Q = 0.5;
/** The note voice's own level; clicks are left as they are. */ const $2adaf782742743f1$var$VOICE_GAIN = 0.8;
/** MIDI note number to frequency in Hz. */ const $2adaf782742743f1$export$7f9f785452ff39e = (pitch)=>440 * Math.pow(2, (pitch - 69) / 12);
/**
 * The v1 NoteVoice: one oscillator per note (not a single shared osc) with
 * a simplified hold-then-decay envelope (ADR 0003, amended twice):
 *
 *   attack  - instantaneous set to peak (the lowpass softens the edge, so
 *             no ramp is needed; targeting any value just re-stated the
 *             fresh node's default)
 *   release - exponential decay toward near-silence, spanning the note
 *             length clamped to 0.7s..2s, plus a fixed 0.5s tail offset
 *   stop    - the oscillator stops 1s past the note's end (grace period),
 *             so a note ending at a bar boundary completes its decay tail
 *             instead of being cut mid-decay
 *
 * The oscillator type and lowpass filter shape the timbre.
 *
 * Shares its AudioContext with ClickPlayer - the scheduler's clock is that
 * context's currentTime, so notes and clicks must schedule against the
 * same clock.
 */ class $2adaf782742743f1$export$7d31ccd2730198a4 {
    audioContext;
    // routed through a single gain node so stop() can silence everything,
    // mirroring ClickPlayer
    gainNode;
    constructor(audioContext){
        this.audioContext = audioContext;
        this.gainNode = this.createGainNode();
    }
    play(pitch, timestamp, lengthSeconds) {
        const oscillator = this.audioContext.createOscillator();
        oscillator.type = $2adaf782742743f1$var$OSC_TYPE;
        oscillator.frequency.value = $2adaf782742743f1$export$7f9f785452ff39e(pitch);
        const filter = this.audioContext.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = $2adaf782742743f1$var$LOWPASS_FREQ;
        filter.Q.value = $2adaf782742743f1$var$LOWPASS_Q;
        const envelope = this.audioContext.createGain();
        this.scheduleEnvelope(envelope.gain, timestamp, lengthSeconds);
        oscillator.connect(filter);
        filter.connect(envelope);
        envelope.connect(this.gainNode);
        oscillator.onended = ()=>{
            oscillator.disconnect();
            filter.disconnect();
            envelope.disconnect();
        };
        oscillator.start(timestamp);
        oscillator.stop(timestamp + lengthSeconds + $2adaf782742743f1$var$OSC_STOP_GRACE_PERIOD);
    }
    /**
     * Holds at peak, then decays exponentially toward near-silence. An
     * exponential ramp must anchor on a non-zero value: anchoring at 0
     * poisons the interpolation (v1/v0 = Infinity, 0 * Infinity = NaN).
     */ scheduleEnvelope(gain, timestamp, lengthSeconds) {
        gain.setValueAtTime(1.0, timestamp);
        const decaySpan = Math.min($2adaf782742743f1$var$RELEASE_MAX_SECONDS, Math.max($2adaf782742743f1$var$RELEASE_MIN_SECONDS, lengthSeconds)) + $2adaf782742743f1$var$RELEASE_TAIL_OFFSET_SECONDS;
        gain.exponentialRampToValueAtTime(0.0001, timestamp + decaySpan);
    }
    stop() {
        this.gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime);
        this.gainNode.disconnect();
        this.gainNode = this.createGainNode();
    }
    createGainNode() {
        const gainNode = new GainNode(this.audioContext, {
            gain: $2adaf782742743f1$var$VOICE_GAIN
        });
        gainNode.connect(this.audioContext.destination);
        return gainNode;
    }
}


/**
 * PhraseCycle - the stateful owner of the phrase/response-bar cycle
 * (ADR 0002): one phrase bar, then responseBars rest bars, repeat. The
 * cycle always starts (and restarts, after reset()) on a phrase bar.
 *
 * It presents the batch-source shape: one nextBatch() call per bar, one
 * bar's events per call. Phrase bars delegate to PhraseGenerator (which
 * stays stateless); rest bars yield a single whole-bar rest, built inline -
 * a rest-generator class would wrap one object literal.
 *
 * responseBars and beatsPerBar are live-read from the config on every
 * nextBatch() call, so changes apply at the next bar boundary. The only
 * state is the bar counter; PhraseCycle knows nothing about scheduling,
 * audio, or clicks.
 */ class $0ba18c241cd6cc75$export$886db50691b90d20 {
    config;
    phraseGenerator;
    barCount = 0;
    constructor(config, phraseGenerator){
        this.config = config;
        this.phraseGenerator = phraseGenerator;
    }
    /** The next bar's events: a phrase batch or a single whole-bar rest. */ nextBatch() {
        const responseBars = Math.max(0, this.config.responseBars);
        const isPhraseBar = this.barCount % (responseBars + 1) === 0;
        this.barCount++;
        if (isPhraseBar) return this.phraseGenerator.generate();
        return [
            {
                kind: 'rest',
                duration: this.config.beatsPerBar
            }
        ];
    }
    /** Restart the cycle: the next batch is a phrase bar. */ reset() {
        this.barCount = 0;
    }
}



/**
 * Bias of the per-phrase target candidate count draw towards maxNoteCount,
 * in percent. 75 weights towards longer phrases while still varying the
 * count; the draw concentrates around 75% of the range, so very short
 * phrases only occur at small caps. Hardcoded for now; a candidate control
 * later.
 */ const $50a8c4dbaf534995$var$NOTE_COUNT_BIAS = 75;
/**
 * Bias of the per-group absorb draw towards absorbing all followers, in
 * percent. 90 weights towards full-length notes so rests are occasional,
 * not the norm. Hardcoded for now; a candidate control later.
 */ const $50a8c4dbaf534995$var$ABSORB_BIAS = 70;
/**
 * PhraseGenerator - produces one phrase bar of MelodyEvents (notes and
 * rests interleaved) on demand.
 *
 * Pipeline per generate() call (ADR 0002 as amended by ADR 0004):
 *
 *   BarTreeGenerator -> flatten (the onset template: one bar of slots)
 *     -> draw a target candidate count in [1, maxNoteCount] (biased towards
 *        maxNoteCount) -> select `target` slots uniformly (candidates)
 *     -> per group (candidate + following unselected slots): draw
 *        K = biasedRandomInt(0, followers, ABSORB_BIAS), absorb the first K
 *        followers into the candidate's duration, emit the remaining
 *        followers as RestEvents
 *     -> one NoteSource.next() per SELECTED slot, in order
 *
 * The config object is held by reference and read on every generate(), so
 * control changes apply to the next phrase - the same live-read pattern as
 * BarTreeGenerator's config and NoteSource's selection. The internal
 * BarTreeGenerator is built with minLeafDepth 0 hardcoded (allowing half
 * notes is Phraser's identity, per ADR 0002), sharing the config's
 * beatsPerBar/granularity/tupletBias by reference so they stay live all
 * the way down.
 *
 * Only selected slots receive pitches; unselected slots are rests and
 * never draw from the NoteSource (preserving the seam for future pitch
 * weighting). NoteSource throws on an empty sounded set; that error is
 * deliberately not caught here (the UI disables play in that state).
 *
 * Slot selection is uniform with no positional weighting: the ADR 0002
 * downbeat guarantee is removed by construction (ADR 0004), so a phrase
 * can start late. The selection/absorb logic lives here, not in the
 * rhythm kernel: articulation is phraser semantics with a single consumer.
 */ class $50a8c4dbaf534995$export$7726fab41516d59 {
    config;
    noteSource;
    barTreeGenerator;
    constructor(config, noteSource){
        this.config = config;
        this.noteSource = noteSource;
        // a live view over the caller's config: getters forward the three
        // shared fields so mutations reach the bar generator, without
        // mutating the caller's object (which Object.assign would) or
        // copying it (which a spread would, breaking liveness)
        this.barTreeGenerator = new (0, $2b01753db8423e69$export$b135b2512b192788)({
            get beatsPerBar () {
                return config.beatsPerBar;
            },
            get granularity () {
                return config.granularity;
            },
            get tupletBias () {
                return config.tupletBias;
            },
            minLeafDepth: 0
        });
    }
    /** Generate one phrase bar: notes and rests interleaved, tiling the bar exactly. */ generate() {
        const maxNoteCount = Math.max(1, this.config.maxNoteCount);
        // a fresh target per phrase: at most the cap, usually close to it
        const target = (0, $910fcc7bce5be3ba$export$b4da2a2d574a34b7)(1, maxNoteCount, $50a8c4dbaf534995$var$NOTE_COUNT_BIAS);
        const slots = (0, $efdf968da99467e3$export$bffa455ba8c619a6)(this.barTreeGenerator.generate(), this.config.beatsPerBar);
        const selected = this.selectSlots(slots.length, target);
        return this.articulate(slots, selected);
    }
    /**
     * Uniform pick of `target` distinct slot indices (partial Fisher-Yates).
     * No positional weighting: slot 0 is as likely to be unselected as any
     * other. Under the target (the rhythm provides fewer slots) every slot
     * is selected - no padding, fewer notes than the target is fine.
     */ selectSlots(slotCount, target) {
        const pickCount = Math.min(target, slotCount);
        const pool = Array.from({
            length: slotCount
        }, (_, i)=>i);
        for(let i = 0; i < pickCount; i++){
            const j = i + Math.floor(Math.random() * (slotCount - i));
            [pool[i], pool[j]] = [
                pool[j],
                pool[i]
            ];
        }
        return new Set(pool.slice(0, pickCount));
    }
    /**
     * Walk the template once. Each selected slot starts a group (the
     * candidate plus the following unselected slots up to the next
     * candidate or the bar end); the group draws
     * K = biasedRandomInt(0, followers, ABSORB_BIAS), absorbs the first K
     * followers' durations into the candidate's note, and emits the
     * remaining followers as rests. Unselected slots with no candidate
     * before them emit leading rests. Absorbed slots always form a prefix
     * of their group and rests a suffix, so a note never spans a gap.
     * Absorption conserves durations exactly; the emitted events tile the
     * bar. Pitches are zipped in here, one next() per candidate, in order.
     */ articulate(slots, selected) {
        const events = [];
        let i = 0;
        while(i < slots.length){
            if (!selected.has(i)) {
                // unselected with no candidate before it: a leading rest
                events.push({
                    kind: 'rest',
                    duration: slots[i].duration
                });
                i++;
                continue;
            }
            let nextCandidate = i + 1;
            while(nextCandidate < slots.length && !selected.has(nextCandidate))nextCandidate++;
            const followers = nextCandidate - i - 1;
            const absorbed = (0, $910fcc7bce5be3ba$export$b4da2a2d574a34b7)(0, followers, $50a8c4dbaf534995$var$ABSORB_BIAS);
            let duration = slots[i].duration;
            for(let f = 0; f < absorbed; f++)duration += slots[i + 1 + f].duration;
            events.push({
                kind: 'note',
                duration: duration,
                pitch: this.noteSource.next()
            });
            for(let f = absorbed; f < followers; f++)events.push({
                kind: 'rest',
                duration: slots[i + 1 + f].duration
            });
            i = nextCandidate;
        }
        return events;
    }
}



/**
 * Onsets within this many beats are the same onset - cumulative float
 * durations can make a theoretically-integer note onset land at
 * 1.9999999999999998.
 */ const $63f1c17262d1a35d$export$d6847dd8514788c8 = 1e-9;
/**
 * Merge one bar's click track and melody into a single onset stream.
 *
 * Beat onsets (one per beat, primary on the downbeat) ALWAYS exist, even
 * when clicks are silenced - silencing drops the click field, never the
 * onset, so a silenced response bar still tiles the timeline. Note onsets
 * come from walking the melody batch and accumulating durations; rests
 * contribute duration but no onset (this handles both response bars and
 * future in-phrase rests - a rest is simply a span with no pitch onset).
 *
 * The result tiles the bar exactly: onsets strictly increasing in
 * [0, beatsPerBar), first at position 0, durations positive and summing to
 * beatsPerBar.
 */ function $63f1c17262d1a35d$export$fb2d02a60a2dad66(beatsPerBar, melody) {
    const onsets = [];
    for(let beat = 0; beat < beatsPerBar; beat++)onsets.push({
        position: beat,
        click: beat === 0 ? 'primary' : 'secondary'
    });
    let position = 0;
    for (const event of melody){
        if (event.kind === 'note') // carry the whole duration as the note's length: the merge
        // truncates the onset event's duration at click onsets, so the
        // full sounding length must travel alongside the pitch
        $63f1c17262d1a35d$var$addNoteOnset(onsets, position, {
            pitch: event.pitch,
            length: event.duration
        });
        position += event.duration;
    }
    return onsets.map((onset, i)=>{
        const nextPosition = i + 1 < onsets.length ? onsets[i + 1].position : beatsPerBar;
        return {
            duration: nextPosition - onset.position,
            ...onset.click !== undefined && {
                click: onset.click
            },
            ...onset.note !== undefined && {
                note: onset.note
            }
        };
    });
}
/** Record a note onset, merging into an existing onset at (within epsilon of) the position. */ function $63f1c17262d1a35d$var$addNoteOnset(onsets, position, note) {
    const existing = onsets.find((o)=>Math.abs(o.position - position) < $63f1c17262d1a35d$export$d6847dd8514788c8);
    if (existing) {
        existing.note = note;
        return;
    }
    const index = onsets.findIndex((o)=>o.position > position);
    onsets.splice(index === -1 ? onsets.length : index, 0, {
        position: position,
        note: note
    });
}
/**
 * The count-in seed: countInBars bars of click-only PhraserEvents, one beat
 * each, primary on each bar's first beat. Count-in bars are exactly normal
 * click bars; passed to scheduler.start(seed), so no count-in logic exists
 * anywhere else. When clicks are silenced the seed stays silent-but-present
 * (durations without click fields), so phrase placement is unchanged.
 */ function $63f1c17262d1a35d$export$6af5bbbe7077263b(countInBars, beatsPerBar, hasClickTrack) {
    const seed = [];
    for(let bar = 0; bar < countInBars; bar++)for(let beat = 0; beat < beatsPerBar; beat++)seed.push({
        duration: 1,
        ...hasClickTrack && {
            click: beat === 0 ? 'primary' : 'secondary'
        }
    });
    return seed;
}


/**
 * The melody/click event source for Phraser's scheduler: one call = one
 * bar, merged from the click track and PhraseCycle's next batch.
 *
 * beatsPerBar and hasClickTrack are live-read from the config on every
 * call (the repo's established live-read pattern). Silencing clicks omits
 * the click FIELD only - because the merged stream tiles time with
 * durations regardless of what sounds, silencing never affects timing.
 *
 * A factory function, not a class: it is stateless itself - all cycle
 * state lives in PhraseCycle.
 */ function $f40c7f1433ab51a4$export$b56e69384ca32ed0(config, phraseCycle) {
    return ()=>{
        const events = (0, $63f1c17262d1a35d$export$fb2d02a60a2dad66)(config.beatsPerBar, phraseCycle.nextBatch());
        if (config.hasClickTrack) return events;
        return events.map((event)=>{
            const silenced = {
                ...event
            };
            delete silenced.click;
            return silenced;
        });
    };
}



/**
 * PhraserPlayer - wires the merged event stream to the kernel Scheduler
 * and dispatches scheduled events to the audio seams.
 *
 * One Scheduler<PhraserEvent>: the merged stream makes a single
 * tempo-conversion point (bpm is live-read per scheduled event), so clicks
 * and notes can never diverge on live tempo changes. currentTime comes from
 * the shared AudioContext (via ClickPlayer), so clicks and notes schedule
 * against the same clock.
 *
 * Count-in is the scheduler's seed: countInBars bars of ordinary click
 * bars. The cycle is untouched by it - PhraseCycle's first pulled batch is
 * a phrase bar, landing immediately after the count-in.
 *
 * Restart semantics: stop/start re-plays the count-in and restarts the
 * cycle on a phrase bar (phraseCycle.reset() on stop, consistent with
 * Clicker's clean restart at a downbeat).
 *
 * All config fields are live-read: bpm per scheduled event (the
 * scheduler's gap conversion and dispatch's note-length conversion, ADR
 * 0003), the rest at the next bar boundary.
 */ class $a216604d178a5a6f$export$5fcd4eaf24466e81 {
    config;
    phraseCycle;
    clickPlayer;
    noteVoice;
    scheduler;
    constructor(config, phraseCycle, clickPlayer, noteVoice){
        this.config = config;
        this.phraseCycle = phraseCycle;
        this.clickPlayer = clickPlayer;
        this.noteVoice = noteVoice;
        this.scheduler = new (0, $c766c855537a2d6d$export$d3e54bb8584cda7c)({
            bpm: ()=>this.config.bpm,
            source: (0, $f40c7f1433ab51a4$export$b56e69384ca32ed0)(this.config, this.phraseCycle)
        }, ()=>this.clickPlayer.currentTime);
        this.scheduler.events$.subscribe(({ event: event, timestamp: timestamp })=>{
            if (event.click !== undefined) this.clickPlayer.click(timestamp, event.click);
            if (event.note !== undefined) // beats -> seconds at dispatch, not in the voice (ADR 0003):
            // the dispatch is synchronous inside the scheduler's
            // scheduling pass, so this bpm read coincides with the
            // scheduler's own gap conversion - the single
            // tempo-conversion-point property survives. The voice stays
            // purely in the audio domain: seconds and timestamps.
            this.noteVoice.play(event.note.pitch, timestamp, event.note.length * 60 / this.config.bpm);
        });
    }
    /** Start playback, with the count-in as the seed. */ start() {
        this.scheduler.start((0, $63f1c17262d1a35d$export$6af5bbbe7077263b)(this.config.countInBars, this.config.beatsPerBar, this.config.hasClickTrack));
    }
    /** Stop playback, silence scheduled audio, and reset the cycle to a phrase bar. */ stop() {
        this.scheduler.stop();
        this.clickPlayer.stop();
        this.noteVoice.stop();
        this.phraseCycle.reset();
    }
}


/**
 * NoteRing - the pitch-class selector visual: a single SVG ring of 12
 * wedges with gaps, labels horizontal inside the wedges, and the two
 * ↻/↷ rotate steppers in the center hole.
 *
 * The component owns geometry only: it renders the wedges once and
 * provides the toggle interaction by callback. The
 * selection-related visual states (selected, dimmed-hollow) are pushed
 * in by the caller through `update`.
 *
 * Geometry is frozen from the prototype (see the ring prototype branch,
 * variant C): ring spans innerFrac=0.61..0.96 of the half-viewBox, 1.0deg
 * gap between wedges, labels at the ring's mid-radius. A center hole large
 * enough for two 4.5rem steppers sits at the middle and is handled by the
 * caller's overlay buttons (this component does not draw them).
 *
 * Dual enharmonic labels (e.g. 'C♯/D♭') render inline: the main name at
 * a fixed rem base, the slash + second name at 0.68em, following variant
 * C of the prototype. Font size uses fixed REM so the label never scales
 * with viewport width.
 */ const $72ed3f96baf1d7e0$var$NS = 'http://www.w3.org/2000/svg';
const $72ed3f96baf1d7e0$var$VIEWBOX = 400;
const $72ed3f96baf1d7e0$var$CENTER = $72ed3f96baf1d7e0$var$VIEWBOX / 2;
const $72ed3f96baf1d7e0$var$OUTER_R = $72ed3f96baf1d7e0$var$CENTER * 0.96;
const $72ed3f96baf1d7e0$var$INNER_R = $72ed3f96baf1d7e0$var$CENTER * 0.61;
const $72ed3f96baf1d7e0$var$GAP_DEG = 1.0; // eaten from each wedge side
function $72ed3f96baf1d7e0$var$polar(radius, angleDeg) {
    const rad = angleDeg * Math.PI / 180;
    return [
        $72ed3f96baf1d7e0$var$CENTER + radius * Math.sin(rad),
        $72ed3f96baf1d7e0$var$CENTER - radius * Math.cos(rad)
    ];
}
class $72ed3f96baf1d7e0$export$156b67657612ad40 {
    callbacks;
    svg;
    /** label list mirrored from the model (used for the aria-labels) */ labels;
    constructor(parent, callbacks, labels){
        this.callbacks = callbacks;
        this.labels = labels;
        this.svg = document.createElementNS($72ed3f96baf1d7e0$var$NS, 'svg');
        this.svg.setAttribute('viewBox', `0 0 ${$72ed3f96baf1d7e0$var$VIEWBOX} ${$72ed3f96baf1d7e0$var$VIEWBOX}`);
        this.svg.classList.add('note-ring');
        parent.prepend(this.svg);
        this.build();
    }
    svgEl(tag) {
        return document.createElementNS($72ed3f96baf1d7e0$var$NS, tag);
    }
    wedgePath(pc) {
        const centerAngle = pc * 30;
        const start = centerAngle - 15 + $72ed3f96baf1d7e0$var$GAP_DEG;
        const end = centerAngle + 15 - $72ed3f96baf1d7e0$var$GAP_DEG;
        const rO = $72ed3f96baf1d7e0$var$OUTER_R;
        const rI = $72ed3f96baf1d7e0$var$INNER_R;
        const [oSx, oSy] = $72ed3f96baf1d7e0$var$polar(rO, start);
        const [oEx, oEy] = $72ed3f96baf1d7e0$var$polar(rO, end);
        const [iEx, iEy] = $72ed3f96baf1d7e0$var$polar(rI, end);
        const [iSx, iSy] = $72ed3f96baf1d7e0$var$polar(rI, start);
        const f = (n)=>n.toFixed(2);
        return `M ${f(oSx)} ${f(oSy)} A ${f(rO)} ${f(rO)} 0 0 1 ${f(oEx)} ${f(oEy)}` + ` L ${f(iEx)} ${f(iEy)} A ${f(rI)} ${f(rI)} 0 0 0 ${f(iSx)} ${f(iSy)} Z`;
    }
    buildLabel(parent, x, y, label) {
        // alphabetic baseline (the default) is shared by the tspans, so the
        // primary and secondary names align on their bottoms. dy=0.35em drops
        // the baseline by half a cap height, visually centering the caps on
        // the wedge's mid-radius point (dominant-baseline: central was the
        // alternative but it aligns middles across mixed font sizes).
        const [main, second] = label.split('/');
        if (!second) {
            const text = this.svgEl('text');
            text.setAttribute('x', x.toFixed(1));
            text.setAttribute('y', y.toFixed(1));
            text.setAttribute('dy', '0.35em');
            text.setAttribute('class', 'ring-label-main');
            text.textContent = main;
            parent.appendChild(text);
            return;
        }
        const text = this.svgEl('text');
        text.setAttribute('x', x.toFixed(1));
        text.setAttribute('y', y.toFixed(1));
        text.setAttribute('dy', '0.35em');
        text.setAttribute('class', 'ring-label-main');
        const primary = this.svgEl('tspan');
        primary.setAttribute('class', 'ring-label-main-part');
        primary.textContent = main;
        const secondary = this.svgEl('tspan');
        secondary.setAttribute('class', 'ring-label-second-part');
        secondary.textContent = `/${second}`;
        text.appendChild(primary);
        text.appendChild(secondary);
        parent.appendChild(text);
    }
    build() {
        for(let pc = 0; pc < 12; pc++){
            const wedge = this.svgEl('g');
            wedge.setAttribute('class', 'ring-wedge');
            wedge.dataset.pc = '' + pc;
            wedge.setAttribute('role', 'button');
            wedge.setAttribute('tabindex', '0');
            wedge.setAttribute('aria-label', this.labels[pc]);
            const path = this.svgEl('path');
            path.setAttribute('d', this.wedgePath(pc));
            path.setAttribute('class', 'ring-wedge-path');
            wedge.appendChild(path);
            const midR = ($72ed3f96baf1d7e0$var$OUTER_R + $72ed3f96baf1d7e0$var$INNER_R) / 2;
            const [x, y] = $72ed3f96baf1d7e0$var$polar(midR, pc * 30);
            this.buildLabel(wedge, x, y, this.labels[pc]);
            wedge.addEventListener('click', ()=>this.callbacks.onToggle(pc));
            wedge.addEventListener('keydown', (event)=>{
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    this.callbacks.onToggle(pc);
                }
            });
            this.svg.appendChild(wedge);
        }
    }
    /** Push selection state into the wedges (the caller computes what's dimmed). */ update(selected, soundedClasses) {
        this.svg.querySelectorAll('.ring-wedge').forEach((wedge)=>{
            const pc = +wedge.dataset.pc;
            const isSelected = selected.has(pc);
            wedge.setAttribute('aria-pressed', '' + isSelected);
            wedge.classList.toggle('ring-dimmed', isSelected && !soundedClasses.has(pc));
        });
    }
}


/**
 * Phraser's composition module: owns its cards' control bindings, the
 * NoteSelection model, and the lazy audio/engine construction (AudioContext
 * created on first user gesture, shared between ClickPlayer and the note
 * voice). The UI stays dumb: all selection semantics live in the model.
 */ /**
 * Note name with register number (e.g. C4, F♯4); the pitch-class label plus
 * the register. Takes the label table from the caller's selection rather
 * than reading the model's private table (a throwaway `new NoteSelection()`
 * per call re-read localStorage for static data).
 */ function $23113b2c511e6d54$var$noteName(n, labels) {
    const mod12 = (n % 12 + 12) % 12;
    const register = Math.floor(n / 12) - 1;
    const label = labels[mod12];
    return label.includes('/') ? label.split('/')[0] + register : label + register;
}
class $23113b2c511e6d54$export$a82bb8cbe3186189 {
    settings;
    selection = new (0, $8c1a597dba073d78$export$c00259035dd4d77e)();
    config;
    audioContext;
    clickPlayer;
    player;
    playing = false;
    noteRing;
    constructor(){
        this.settings = $23113b2c511e6d54$var$loadPhraserSettings();
        this.config = {
            bpm: this.settings.bpm,
            beatsPerBar: this.settings.beatsPerBar,
            countInBars: this.settings.countInBars,
            hasClickTrack: this.settings.hasClickTrack,
            responseBars: this.settings.responseBars,
            granularity: this.settings.granularity,
            tupletBias: this.settings.tupletBias,
            maxNoteCount: (0, $c38d17e4adf57e4d$export$86a570d26acd92b7)(this.settings.density, this.settings.beatsPerBar)
        };
        this.initialise();
    }
    get isPlaying() {
        return this.playing;
    }
    /**
     * The engine and audio are created lazily, on the first user interaction
     * that needs audio (pressing play, or picking a sound in the Settings
     * sheet), so that the AudioContext is always created inside a user
     * gesture. Both players share one AudioContext: the scheduler's clock is
     * that context's currentTime.
     * @private
     */ getOrCreatePlayer() {
        if (!this.player) {
            this.audioContext = new AudioContext();
            this.clickPlayer = new (0, $c523d1e73d11fc5d$export$ce4d7b336b893c95)(this.settings.downbeatSound, this.settings.secondarySound, this.audioContext);
            const noteVoice = new (0, $2adaf782742743f1$export$7d31ccd2730198a4)(this.audioContext);
            const noteSource = new (0, $7b29b154c8c1639f$export$e4d8dd7e6c5dcdcc)(this.selection);
            const phraseGenerator = new (0, $50a8c4dbaf534995$export$7726fab41516d59)(this.config, noteSource);
            const phraseCycle = new (0, $0ba18c241cd6cc75$export$886db50691b90d20)(this.config, phraseGenerator);
            this.player = new (0, $a216604d178a5a6f$export$5fcd4eaf24466e81)(this.config, phraseCycle, this.clickPlayer, noteVoice);
        }
        return this.player;
    }
    getOrCreateClickPlayer() {
        this.getOrCreatePlayer();
        return this.clickPlayer;
    }
    start() {
        this.playing = true;
        this.getOrCreatePlayer();
        this.clickPlayer.resume().then(()=>this.player?.start());
    }
    stop() {
        this.playing = false;
        this.player?.stop();
        this.syncPlayControl();
    }
    onSoundSelected(section, sound) {
        const key = section === 'downbeat' ? 'downbeatSound' : 'secondarySound';
        this.settings[key] = sound;
        localStorage.setItem(key, sound);
        const audio = this.getOrCreateClickPlayer();
        if (section === 'downbeat') audio.setPrimarySound(sound);
        else audio.setSecondarySound(sound);
        if (!this.isPlaying) audio.preview(sound);
    }
    // ---------------------------------------------------------------------
    // UI wiring
    // ---------------------------------------------------------------------
    initialise() {
        const playCheckbox = document.getElementById('phraser-play-checkbox');
        if (playCheckbox) playCheckbox.addEventListener('click', (event)=>{
            const isPlaying = event.target.checked;
            if (isPlaying) this.start();
            else this.stop();
            this.syncPlayControl();
        });
        const bindSetting = (id, key)=>(0, $d37efb89f06c4220$export$4fc2d638ed78045a)(id, ()=>this.settings[key], (value)=>{
                localStorage.setItem(`phraser.${key}`, '' + value);
                this.settings[key] = value;
                this.applySetting(key);
            });
        bindSetting('phraser-bpm', 'bpm');
        (0, $d37efb89f06c4220$export$e0a99e6a99ef25a0)('phraser-bpm-output', 'phraser-bpm-input', 'phraser-bpm');
        bindSetting('phraser-beatsPerBar', 'beatsPerBar');
        bindSetting('phraser-countInBars', 'countInBars');
        bindSetting('phraser-responseBars', 'responseBars');
        bindSetting('phraser-density', 'density');
        bindSetting('phraser-granularity', 'granularity');
        bindSetting('phraser-tuplets', 'tupletBias');
        (0, $d37efb89f06c4220$export$913f1d0f2a072d8c)('phraser-hasClickTrack', ()=>this.settings.hasClickTrack, (isChecked)=>{
            localStorage.setItem('phraser.hasClickTrack', JSON.stringify(isChecked));
            this.settings.hasClickTrack = isChecked;
            this.config.hasClickTrack = isChecked;
        });
        this.initialiseNoteSelectionCard();
        (0, $d37efb89f06c4220$export$c2f691affe889eb1)(document.getElementById('tool-phraser'));
        this.renderNoteSelectionCard();
        this.syncPlayControl();
    }
    /** Push a changed setting into the live config object the engine reads. */ applySetting(key) {
        switch(key){
            case 'bpm':
                this.config.bpm = this.settings.bpm;
                break;
            case 'beatsPerBar':
                this.config.beatsPerBar = this.settings.beatsPerBar;
                this.config.maxNoteCount = (0, $c38d17e4adf57e4d$export$86a570d26acd92b7)(this.settings.density, this.settings.beatsPerBar);
                break;
            case 'density':
                this.config.maxNoteCount = (0, $c38d17e4adf57e4d$export$86a570d26acd92b7)(this.settings.density, this.settings.beatsPerBar);
                break;
            case 'countInBars':
                this.config.countInBars = this.settings.countInBars;
                break;
            case 'responseBars':
                this.config.responseBars = this.settings.responseBars;
                break;
            case 'granularity':
                this.config.granularity = this.settings.granularity;
                break;
            case 'tupletBias':
                this.config.tupletBias = this.settings.tupletBias;
                break;
        }
    }
    // ---------------------------------------------------------------------
    // Note selection card
    // ---------------------------------------------------------------------
    initialiseNoteSelectionCard() {
        // the ring sits in #phraser-notes; the ↻/↷ buttons live in the center hole
        const notes = document.getElementById('phraser-notes');
        this.noteRing = new (0, $72ed3f96baf1d7e0$export$156b67657612ad40)(notes, {
            onToggle: (pc)=>{
                this.selection.togglePitchClass(pc);
                this.renderNoteSelectionCard();
            }
        }, this.selection.pitchClassLabels());
        // Rotation is stateless: each click applies a bijective rotation to
        // the selection itself; the checkmarks orbit the clock face. The
        // buttons are never disabled (rotating an empty selection is a
        // harmless no-op).
        document.getElementById('phraser-rotate-anticlockwise').addEventListener('click', ()=>{
            this.selection.rotate(-1);
            this.renderNoteSelectionCard();
        });
        document.getElementById('phraser-rotate-clockwise').addEventListener('click', ()=>{
            this.selection.rotate(1);
            this.renderNoteSelectionCard();
        });
        const rangeLow = document.getElementById('phraser-range-low');
        rangeLow.addEventListener('input', ()=>{
            this.selection.setRangeLow(rangeLow.valueAsNumber);
            this.renderNoteSelectionCard();
        });
        const rangeHigh = document.getElementById('phraser-range-high');
        rangeHigh.addEventListener('input', ()=>{
            this.selection.setRangeHigh(rangeHigh.valueAsNumber);
            this.renderNoteSelectionCard();
        });
        // Thumb grabs only: the track itself is inert. On touch, a track
        // press is almost always a scroll gesture that happens to start on
        // the slider, and snapping a thumb to it would silently rewrite
        // the window (native single-thumb sliders get the same treatment
        // via guardRangeTrackPress in shared/controls). The container has
        // touch-action: pan-y, so vertical scrolls pass over the track
        // while horizontal thumb drags stay with the control.
        //
        // Thumb grabs take the native path, with one fix-up: when the
        // thumbs overlap (coincident or within a thumb width), hit-testing
        // hands the press to the input on top in z-order regardless of
        // which thumb the user aimed at, and the top input's native drag
        // clamps against the other thumb - the window cannot be widened by
        // dragging. Pointer capture does not retarget Chromium's native
        // slider drag, so a misrouted grab is dragged manually: the
        // intended thumb follows the pointer (mapped through the same
        // offset scale) until pointerup. Grabs that hit the right input
        // keep the fully native drag.
        const slider = document.querySelector('.range-slider');
        slider.addEventListener('pointerdown', (event)=>{
            const rect = slider.getBoundingClientRect();
            // the inputs' thumb centers travel half a thumb width (12px)
            // in from the edges; map press positions onto the same scale
            const offsetAt = (clientX)=>{
                const fraction = (clientX - rect.left - 12) / (rect.width - 24);
                return Math.round(Math.min(Math.max(fraction, 0), 1) * 36);
            };
            const offset = offsetAt(event.clientX);
            const [low, high] = this.selection.rangeOffsets();
            const onLowThumb = Math.abs(offset - low) <= 1;
            const onHighThumb = Math.abs(offset - high) <= 1;
            if (event.target instanceof HTMLInputElement && (onLowThumb || onHighThumb)) {
                // a thumb grab: which thumb did the user aim at? Compare the
                // raw (unrounded) press position against the thumbs' centers
                // - at coincidence the press side decides, so a grab just
                // right of the overlapped thumbs takes the high thumb, just
                // left takes the low one. A press dead center goes low.
                const rawOffset = (event.clientX - rect.left - 12) / (rect.width - 24) * 36;
                const intended = onLowThumb && (!onHighThumb || rawOffset - low <= high - rawOffset) ? rangeLow : rangeHigh;
                if (event.target !== intended) {
                    // the wrong input is on top: suppress its native drag
                    // and move the intended thumb manually until pointerup.
                    // A preventDefault'ed press gets no implicit pointer
                    // capture, so the moves go to whatever is under the
                    // pointer - listening on window (bubble phase) catches
                    // them wherever they land
                    event.preventDefault();
                    intended.focus();
                    const setThumb = intended === rangeLow ? (o)=>this.selection.setRangeLow(o) : (o)=>this.selection.setRangeHigh(o);
                    const onMove = (moveEvent)=>{
                        setThumb(offsetAt(moveEvent.clientX));
                        this.renderNoteSelectionCard();
                    };
                    const onUp = ()=>{
                        window.removeEventListener('pointermove', onMove);
                        window.removeEventListener('pointerup', onUp);
                        window.removeEventListener('pointercancel', onUp);
                    };
                    window.addEventListener('pointermove', onMove);
                    window.addEventListener('pointerup', onUp);
                    window.addEventListener('pointercancel', onUp);
                }
                return;
            }
        // anything else is a track press: the track is inert, so the
        // gesture falls through to the page (scroll on touch, nothing
        // on mouse)
        });
        document.getElementById('phraser-octave-down').addEventListener('click', ()=>{
            this.selection.setOctave(this.selection.getOctave() - 1);
            this.renderNoteSelectionCard();
        });
        document.getElementById('phraser-octave-up').addEventListener('click', ()=>{
            this.selection.setOctave(this.selection.getOctave() + 1);
            this.renderNoteSelectionCard();
        });
    }
    /**
     * Re-render checkmarks (selected + dimmed states), thumb positions and
     * note-name readouts after every mutation: rotation moves checkmarks,
     * octave moves note names, range moves thumbs and can change which
     * selected classes are dimmed. Labels never move.
     */ renderNoteSelectionCard() {
        const sounded = this.selection.soundedNotes();
        const soundedClasses = new Set(sounded.map((n)=>(n % 12 + 12) % 12));
        const selected = new Set();
        for(let pc = 0; pc < 12; pc++)if (this.selection.isSelected(pc)) selected.add(pc);
        this.noteRing.update(selected, soundedClasses);
        const [lowOffset, highOffset] = this.selection.rangeOffsets();
        const rangeLow = document.getElementById('phraser-range-low');
        rangeLow.valueAsNumber = lowOffset;
        const labels = this.selection.pitchClassLabels();
        rangeLow.setAttribute('aria-valuetext', $23113b2c511e6d54$var$noteName(this.selection.rangeWindow()[0], labels));
        const rangeHigh = document.getElementById('phraser-range-high');
        rangeHigh.valueAsNumber = highOffset;
        rangeHigh.setAttribute('aria-valuetext', $23113b2c511e6d54$var$noteName(this.selection.rangeWindow()[1], labels));
        // the window fill between the thumbs: thumb centers travel half a
        // thumb width in from the track ends, so anchor the fill to the
        // thumb-center line, not the track ends (percentages alone would
        // misalign the fill edges with the thumbs)
        const fill = document.getElementById('phraser-range-fill');
        fill.style.left = `calc(0.75rem + (100% - 1.5rem) * ${lowOffset / 36})`;
        fill.style.right = `calc(0.75rem + (100% - 1.5rem) * ${(36 - highOffset) / 36})`;
        const [low, high] = this.selection.rangeWindow();
        document.getElementById('phraser-range-output').textContent = `${$23113b2c511e6d54$var$noteName(low, labels)} - ${$23113b2c511e6d54$var$noteName(high, labels)}`;
        document.getElementById('phraser-octave-output').textContent = '' + this.selection.getOctave();
        this.syncPlayControl();
    }
    /**
     * Keep the play toggle's visual/aria state in sync. The play control is
     * disabled whenever the sounded set is empty (strategy A); if the
     * selection is emptied mid-play, stop.
     */ syncPlayControl() {
        const playCheckbox = document.getElementById('phraser-play-checkbox');
        const playControl = playCheckbox?.closest('.play-control');
        if (!playCheckbox || !playControl) return;
        const canPlay = this.selection.soundedNotes().length > 0;
        if (!canPlay && this.playing) {
            this.stop();
            return;
        }
        playCheckbox.disabled = !canPlay;
        playControl.classList.toggle('disabled', !canPlay);
        playControl.setAttribute('aria-disabled', '' + !canPlay);
        playCheckbox.checked = this.playing;
        playControl.classList.toggle('playing', this.playing);
        playControl.setAttribute('aria-label', this.playing ? 'Stop' : 'Play');
    }
}
/** Load Phraser's settings from localStorage (phraser.-prefixed keys). */ function $23113b2c511e6d54$var$loadPhraserSettings() {
    return {
        bpm: +(localStorage.getItem('phraser.bpm') || 80),
        beatsPerBar: +(localStorage.getItem('phraser.beatsPerBar') || 4),
        countInBars: +(localStorage.getItem('phraser.countInBars') || 1),
        responseBars: +(localStorage.getItem('phraser.responseBars') || 1),
        hasClickTrack: localStorage.getItem('phraser.hasClickTrack') !== 'false',
        density: +(localStorage.getItem('phraser.density') || 25),
        granularity: +(localStorage.getItem('phraser.granularity') || 25),
        tupletBias: +(localStorage.getItem('phraser.tupletBias') || 1),
        downbeatSound: (0, $b7bbc4f8d421aeee$export$8bcf5b6f3ecd9d6d)(localStorage.getItem('downbeatSound'), (0, $b7bbc4f8d421aeee$export$eb5f9c6e38f3e159)),
        secondarySound: (0, $b7bbc4f8d421aeee$export$8bcf5b6f3ecd9d6d)(localStorage.getItem('secondarySound'), (0, $b7bbc4f8d421aeee$export$67fdbea1e102c657))
    };
}


/**
 * The suite shell: owns the header, the Phraser | Clicker tab strip, and the
 * settings sheet (app-level shared click sound selection), and switches the
 * active tool.
 *
 * Switching stops the playing tool, with no warning - playback is ephemeral
 * and a confirm dialog would be hostile. The active tab persists
 * (localStorage `activeTool`), so the PWA reopens on the last-used tool.
 */ class $f84ca543e6edd162$var$Shell {
    tools;
    tabs;
    activeToolId;
    constructor(){
        this.tools = {
            clicker: new (0, $b01c745e30ba2459$export$5d0071c67bc537b2)(),
            phraser: new (0, $23113b2c511e6d54$export$a82bb8cbe3186189)()
        };
        this.tabs = Array.from(document.querySelectorAll('.tool-tab'));
        this.activeToolId = this.loadActiveTool();
        this.initialiseTabs();
        this.show(this.activeToolId);
        new (0, $d8caa695d74baecf$export$ee3e5af31f081424)({
            getSelection: (section)=>section === 'downbeat' ? (0, $b7bbc4f8d421aeee$export$8bcf5b6f3ecd9d6d)(localStorage.getItem('downbeatSound'), (0, $b7bbc4f8d421aeee$export$eb5f9c6e38f3e159)) : (0, $b7bbc4f8d421aeee$export$8bcf5b6f3ecd9d6d)(localStorage.getItem('secondarySound'), (0, $b7bbc4f8d421aeee$export$67fdbea1e102c657)),
            onSelect: (section, sound)=>this.onSoundSelected(section, sound)
        });
    }
    /** Route a sound pick to the active tool; it persists, swaps, and previews. */ onSoundSelected(section, sound) {
        this.tools[this.activeToolId].onSoundSelected(section, sound);
    }
    initialiseTabs() {
        this.tabs.forEach((tab)=>{
            tab.addEventListener('click', ()=>this.switchTo(tab.dataset.tool));
        });
        // tablist arrow-key navigation, with automatic activation
        document.querySelector('.tool-tabs').addEventListener('keydown', (event)=>{
            const keyEvent = event;
            if (keyEvent.key !== 'ArrowLeft' && keyEvent.key !== 'ArrowRight') return;
            keyEvent.preventDefault();
            const index = this.tabs.indexOf(keyEvent.target);
            if (index === -1) return;
            const direction = keyEvent.key === 'ArrowLeft' ? -1 : 1;
            const next = this.tabs[(index + direction + this.tabs.length) % this.tabs.length];
            this.switchTo(next.dataset.tool);
            next.focus();
        });
    }
    /** Switch tools: stop the outgoing one (no warning), hide it, show the incoming one. */ switchTo(toolId) {
        if (toolId === this.activeToolId) return;
        this.tools[this.activeToolId].stop();
        this.activeToolId = toolId;
        localStorage.setItem('activeTool', toolId);
        this.show(toolId);
    }
    /** Sync tab states and panel visibility with the active tool. */ show(toolId) {
        this.tabs.forEach((tab)=>{
            const isActive = tab.dataset.tool === toolId;
            tab.classList.toggle('active', isActive);
            tab.setAttribute('aria-selected', '' + isActive);
            tab.tabIndex = isActive ? 0 : -1;
            document.getElementById(tab.getAttribute('aria-controls')).hidden = !isActive;
        });
    }
    loadActiveTool() {
        return localStorage.getItem('activeTool') === 'clicker' ? 'clicker' : 'phraser';
    }
}
new $f84ca543e6edd162$var$Shell();
// Offline support (PWA). Production builds only: the service worker is
// generated into dist/ by scripts/generate-sw.cjs, and registering it against
// the dev server would fight hot reload. When a new version's worker is
// waiting to activate, the user gets an "Update available" prompt; accepting
// activates the worker and reloads once onto the new version.
if ('serviceWorker' in navigator) window.addEventListener('load', ()=>{
    const prompt = new (0, $858d7a32f3befc68$export$f16884506f2c54fe)();
    (0, $b296d3f904179b79$export$c6245babf0c60892)({
        onUpdateAvailable: (waiting)=>prompt.show(()=>(0, $b296d3f904179b79$export$cadaaf14c55f94de)(waiting))
    }).catch((error)=>console.warn('Service worker registration failed:', error));
});


//# sourceMappingURL=measure-up.3465d082.js.map
